import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Filter, X, Search, Edit, Trash2, ArrowUpDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { deleteProject } from "../../FeatureRedux/project/deleteProject";
import axiosInstance from "../axiosInstance";

const ProjectList = ({ onEditProject, reload }) => {
  const [projectList, setProjectList] = useState([]);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [filterDropdown, setFilterDropdown] = useState(null);
  const [searchQuery, setSearchQuery] = useState(""); 
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const itemsPerPage = 10;  
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const modalRef = useRef(null);
  const tableRef = useRef(null);
  const dispatch = useDispatch();
  const { isLoading, isError, errorMessage, issuscess } = useSelector((state) => state.deleteProject);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axiosInstance.get('/project/allProjects', {
          headers: {
            'x-company-id': '67ce66294f28f85e4ff91e2c'
          }
        });
        setProjectList(response.data.projects);
      } catch (error) {
        console.error('Error fetching projects:', error.response?.data || error.message);
      }
    };
    fetchProjects();
  }, [reload, issuscess]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setFilterDropdown(null);
      }
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowDeleteModal(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (showDeleteModal && event.key === 'Enter') {
        confirmDelete();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDeleteModal, projectToDelete]);

  const handleProjectClick = (project) => {
    navigate('/detail', { state: { project } });
  };

  const handleEditClick = (project, e) => {
    e.stopPropagation();
    onEditProject(project);
  };

  const handleDeleteClick = (e, project) => {
    e.stopPropagation();
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (projectToDelete) {
      dispatch(deleteProject(projectToDelete._id));
      setShowDeleteModal(false);
      setProjectToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setProjectToDelete(null);
  };

  const handleFilter = (key, value) => {
    const propertyMap = {
      'Area': 'area',
      'Unit': 'areaUnit'
    };
    const actualKey = propertyMap[key] || key;
    
    setFilters((prevFilters) => ({
      ...prevFilters,
      [actualKey]: value,
    }));
    setFilterDropdown(null);
    setCurrentPage(1);
  };

  const removeFilter = (key) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters };
      delete newFilters[key];
      return newFilters;
    });
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const filteredData = projectList.filter((item) => {
    return Object.keys(filters).every((key) => {
      if (!filters[key]) return true;
      const itemValue = item[key];
      const filterValue = filters[key];
      
      if (itemValue == null || filterValue == null) return false;
      
      if (typeof itemValue === "number") {
        return itemValue === parseFloat(filterValue);
      }
      
      const itemStr = String(itemValue).toLowerCase();
      const filterStr = String(filterValue).toLowerCase();
      
      return itemStr.includes(filterStr);
    });
  });

  const sortedData = [...filteredData].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const currentData = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (tableRef.current) {
      tableRef.current.scrollTop = 0;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(value);
  };

  const getUniqueValues = (key) => {
    const propertyMap = {
      'Area': 'area',
      'Unit': 'areaUnit'
    };
    const actualKey = propertyMap[key] || key;
    
    const values = projectList.map((item) => item[actualKey]);
    const uniqueValues = [...new Set(values)].filter((value) => {
      if (value === undefined || value === null) return false;
      if (typeof value === "number") {
        return value.toString().includes(searchQuery);
      }
      return String(value).toLowerCase().includes(searchQuery.toLowerCase());
    });
    return uniqueValues.slice(0, 4);
  };

  const getShortName = (value) => {
    if (!value) return "";
    if (value.length > 8) return `${value.substring(0, 8)}...`;
    if (value === "India") return "IN";
    if (value === "United States") return "US";
    if (value === "United Kingdom") return "UK";
    if (value === "Maharashtra") return "MH";
    if (value === "California") return "CA";
    if (value === "New York") return "NY";
    if (value === "Mumbai") return "MUM";
    if (value === "Los Angeles") return "LA";
    return value;
  };

  const truncateText = (text) => {
    if (!text) return "";
    return text.length > 8 ? `${text.substring(0, 8)}...` : text;
  };

  const formatDate = (dateString) => {
    if (!dateString) return ""; 
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisibleButtons = 5;
    
    buttons.push(
      <button
        key={1}
        onClick={() => handlePageChange(1)}
        className={`px-2 py-1 rounded text-xs font-medium transition-all border ${
          currentPage === 1
            ? "bg-blue-500 text-white border-blue-600"
            : "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
        }`}
      >
        1
      </button>
    );

    let startPage = Math.max(2, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage <= 3) {
      endPage = Math.min(4, totalPages - 1);
    }
    else if (currentPage >= totalPages - 2) {
      startPage = Math.max(totalPages - 3, 2);
    }

    if (startPage > 2) {
      buttons.push(
        <span key="start-ellipsis" className="px-1 py-1 text-gray-400">
          ...
        </span>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-2 py-1 rounded text-xs font-medium transition-all border ${
            currentPage === i
              ? "bg-blue-500 text-white border-blue-600"
              : "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
          }`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages - 1) {
      buttons.push(
        <span key="end-ellipsis" className="px-1 py-1 text-gray-400">
          ...
        </span>
      );
    }

    if (totalPages > 1) {
      buttons.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className={`px-2 py-1 rounded text-xs font-medium transition-all border ${
            currentPage === totalPages
              ? "bg-blue-500 text-white border-blue-600"
              : "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
          }`}
        >
          {totalPages}
        </button>
      );
    }    

    return buttons;
  };

  return (
    <div className="relative w-full h-[400px] bg-gray-900 shadow-md rounded-lg border border-gray-700 flex flex-col" >
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            ref={modalRef}
            className="bg-gray-800 rounded-lg p-6 w-full max-w-md transform transition-all border border-gray-700"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-white">Confirm Deletion</h3>
              <button 
                onClick={cancelDelete}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete project "{projectToDelete?.project_name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition-colors border border-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center border border-red-700"
                disabled={isLoading}
                autoFocus // This makes the delete button focused by default
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rest of your component remains exactly the same */}
      {Object.keys(filters).length > 0 && (
        <div className="p-2 bg-gray-800 border-b border-gray-700 flex flex-wrap items-center gap-2 sticky top-0 z-10">
          <span className="text-xs text-gray-300 mr-1">Filters:</span>
          {Object.entries(filters).map(([key, value]) => (
            <div 
              key={key} 
              className="flex items-center bg-gray-700 rounded px-2 py-0.5 text-xs text-white border border-gray-600"
            >
              <span className="capitalize mr-1">{key.replace(/_/g, " ")}:</span>
              <span>{truncateText(value)}</span>
              <button 
                onClick={() => removeFilter(key)}
                className="ml-1 text-gray-400 hover:text-white"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <button 
            onClick={resetFilters}
            className="ml-1 text-xs text-blue-400 hover:text-blue-300 border border-blue-500 rounded px-1.5 py-0.5"
          >
            Clear all
          </button>
        </div>
      )}

      <div className="flex-1 overflow-hidden">
        <div className="w-full h-full overflow-x-auto">
          <div className="w-[100%]">
            <table className="min-w-max w-full border-separate border-spacing-0">
              <thead className="bg-gray-900 text-xs font-semibold text-white sticky top-0 z-10" data-aos='fade-down'  data-aos-delay="300">
                <tr>
                  {[
                    "project_name",
                    "sector",
                    "Area",
                    "Unit",
                    "cost",
                    "status",
                    "country",
                    "state",
                    "city",
                    "startDate",
                    "endDate",
                    "contractor",
                    "action",
                  ].map((header) => (
                    <th 
                      key={header} 
                      className="p-2 text-left border-b border-t border-l border-gray-700 first:rounded-tl-lg last:rounded-tr-lg last:border-r relative whitespace-nowrap"
                    >
                      <div className="flex items-center">
                        <span className="uppercase mr-1">{header.replace(/_/g, " ")}</span>
                        {header !== "action" && (
                          <div className="flex items-center space-x-0.5">
                            <button
                              onClick={() => setFilterDropdown(header === filterDropdown ? null : header)}
                              className="p-0.5 hover:bg-gray-800 rounded"
                            >
                              <Filter className="w-3 h-3 text-gray-500" />
                            </button>
                            {header === "cost" && (
                              <button
                                onClick={() => handleSort(header)}
                                className="p-0.5 hover:bg-gray-800 rounded"
                              >
                                <ArrowUpDown className="w-3 h-3 text-gray-500" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {filterDropdown === header && (
                        <div 
                          ref={dropdownRef} 
                          className="absolute left-0 mt-1 bg-gray-800 text-white border border-gray-700 rounded-md shadow-lg z-20 w-40 max-h-60 overflow-hidden"
                          style={{ top: "100%" }}
                        >
                          <div className="p-1 border-b border-gray-700">
                            <div className="relative">
                              <Search className="absolute left-2 top-2 h-3 w-3 text-gray-400" />
                              <input
                                type="text"
                                placeholder="Search..."
                                className="w-full pl-7 pr-1 py-1 bg-gray-700 border border-gray-600 rounded text-xs text-white mb-0.5"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                              />
                            </div>
                          </div>
                          <div className="overflow-y-auto max-h-48">
                            {getUniqueValues(header).length > 0 ? (
                              getUniqueValues(header).map((value) => (
                                <div
                                  key={value}
                                  className="px-2 py-1 text-xs hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0 truncate"
                                  onClick={() => handleFilter(header, value)}
                                  title={value}
                                >
                                  {value}
                                </div>
                              ))
                            ) : (
                              <div className="px-2 py-1 text-xs text-gray-400 text-center">
                                No matches
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody ref={tableRef}>
                {currentData.length > 0 ? (
                  currentData.map((item, rowIndex) => (
                    <tr
                      key={item._id}
                      className="hover:bg-gray-800 cursor-pointer transition-all"
                      onClick={() => handleProjectClick(item)} data-aos="fade-up"  data-aos-delay="700"
                    >
                      {[
                        <span title={item.project_name} className="block py-1">{truncateText(item.project_name)}</span>,
                        <span title={item.sector} className="block py-1">{truncateText(item.sector)}</span>,
                        truncateText(item.area || "-"),
                        truncateText(item.areaUnit || "-"),
                        formatCurrency(item.cost || "-"),
                        truncateText(item.status || "-"),
                        <span title={item.country}>{getShortName(item.country) || "-"}</span>,
                        <span title={item.state}>{getShortName(item.state) || "-"}</span>,
                        <span title={item.city}>{getShortName(item.city) || "-"}</span>,
                        formatDate(item.startDate || "-"),
                        formatDate(item.endDate || "-"),
                        <span title={item.contractor}>{truncateText(item.contractor || "-")}</span>,
                        <div key="action" className="flex space-x-3 items-start justify-start py-1">
                          <button
                            onClick={(e) => handleEditClick(item, e)}
                            className="text-blue-500 hover:text-blue-700"
                          >
                            <Edit className="w-3 h-3" />
                          </button>
                          <button
                            className="text-red-500 hover:text-red-700"
                            onClick={(e) => handleDeleteClick(e, item)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>,
                      ].map((value, colIndex) => (
                        <td 
                          key={colIndex} 
                          className={`p-0 text-xs text-white border-b border-l border-gray-700 
                            ${colIndex === 0 ? "border-l-0" : ""}
                            ${colIndex === 10 ? "border-r border-r-gray-700" : ""}
                            ${rowIndex === currentData.length - 1 ? "border-b" : ""}
                            ${rowIndex === currentData.length - 1 && colIndex === 0 ? "rounded-bl-lg" : ""}
                            ${rowIndex === currentData.length - 1 && colIndex === 10 ? "rounded-br-lg" : ""}
                          `}
                        >
                          <div className="p-2">{value}</div>
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td 
                      colSpan={13} 
                      className="text-center py-3 text-gray-500 border border-gray-700 text-xs"
                    >
                      {Object.keys(filters).length > 0 ? "No matching projects found" : "No data available"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center py-2 px-3 bg-gray-900 border-t border-gray-700 rounded-b-lg">
        <div className="text-xs text-gray-400">
          Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, sortedData.length)} of {sortedData.length} entries
        </div>
        <div className="flex gap-1 items-center">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            className="px-2 py-1 rounded bg-gray-700 text-white flex items-center disabled:opacity-50 border border-gray-600 text-xs"
            disabled={currentPage === 1}
          >
            <ChevronLeft className="w-3 h-3 mr-0.5" /> Prev
          </button>

          <div className="flex space-x-0.5">
            {renderPaginationButtons()}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            className="px-2 py-1 rounded bg-gray-700 text-white flex items-center disabled:opacity-50 border border-gray-600 text-xs"
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next <ChevronRight className="w-3 h-3 ml-0.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectList;
// import React, { useState, useEffect, useRef } from "react";
// import axios from "axios";
// import { ChevronLeft, ChevronRight, Filter, X, Search, Edit, Trash2, ArrowUpDown } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { deleteProject } from "../../FeatureRedux/project/deleteProject";
// import axiosInstance from "../axiosInstance";

// const ProjectList = ({ onEditProject, reload }) => {
//   const [projectList, setProjectList] = useState([]);
//   const [filters, setFilters] = useState({});
//   const [currentPage, setCurrentPage] = useState(1);
//   const [filterDropdown, setFilterDropdown] = useState(null);
//   const [searchQuery, setSearchQuery] = useState(""); 
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [projectToDelete, setProjectToDelete] = useState(null);
//   const itemsPerPage = 10;  
//   const navigate = useNavigate();
//   const dropdownRef = useRef(null);
//   const modalRef = useRef(null);
//   const tableRef = useRef(null);
//   const dispatch = useDispatch();
//   const { isLoading, isError, errorMessage, issuscess } = useSelector((state) => state.deleteProject);
//   useEffect(() => {
//     const fetchProjects = async () => {
//       try {
//         const response = await axiosInstance.get('/project/allProjects', {
//           headers: {
//             'x-company-id': '67ce66294f28f85e4ff91e2c'
//           }
//         });
//         setProjectList(response.data.projects);
//       } catch (error) {
//         console.error('Error fetching projects:', error.response?.data || error.message);
//       }
//     };
//     fetchProjects();
//   }, [reload, issuscess]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setFilterDropdown(null);
//       }
//       if (modalRef.current && !modalRef.current.contains(event.target)) {
//         setShowDeleteModal(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   const handleProjectClick = (project) => {
//     navigate('/detail', { state: { project } });
//   };

//   const handleEditClick = (project, e) => {
//     e.stopPropagation();
//     onEditProject(project);
//   };

//   const handleDeleteClick = (e, project) => {
//     e.stopPropagation();
//     setProjectToDelete(project);
//     setShowDeleteModal(true);
//   };

//   const confirmDelete = () => {
//     if (projectToDelete) {
//       dispatch(deleteProject(projectToDelete._id));
//       setShowDeleteModal(false);
//       setProjectToDelete(null);
//     }
//   };

//   const cancelDelete = () => {
//     setShowDeleteModal(false);
//     setProjectToDelete(null);
//   };

//   const handleFilter = (key, value) => {
//     // Map the header names to the actual property names
//     const propertyMap = {
//       'Area': 'area',
//       'Unit': 'areaUnit'
//     };
//     const actualKey = propertyMap[key] || key;
    
//     setFilters((prevFilters) => ({
//       ...prevFilters,
//       [actualKey]: value,
//     }));
//     setFilterDropdown(null);
//     setCurrentPage(1);
//   };

//   const removeFilter = (key) => {
//     setFilters((prevFilters) => {
//       const newFilters = { ...prevFilters };
//       delete newFilters[key];
//       return newFilters;
//     });
//     setCurrentPage(1);
//   };

//   const resetFilters = () => {
//     setFilters({});
//     setCurrentPage(1);
//   };

//   const handleSort = (key) => {
//     let direction = "asc";
//     if (sortConfig.key === key && sortConfig.direction === "asc") {
//       direction = "desc";
//     }
//     setSortConfig({ key, direction });
//   };

//   const filteredData = projectList.filter((item) => {
//     return Object.keys(filters).every((key) => {
//       if (!filters[key]) return true;
//       const itemValue = item[key];
//       const filterValue = filters[key];
      
//       if (itemValue == null || filterValue == null) return false;
      
//       if (typeof itemValue === "number") {
//         return itemValue === parseFloat(filterValue);
//       }
      
//       const itemStr = String(itemValue).toLowerCase();
//       const filterStr = String(filterValue).toLowerCase();
      
//       return itemStr.includes(filterStr);
//     });
//   });

//   const sortedData = [...filteredData].sort((a, b) => {
//     if (sortConfig.key) {
//       const aValue = a[sortConfig.key];
//       const bValue = b[sortConfig.key];
//       if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
//       if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
//     }
//     return 0;
//   });

//   const totalPages = Math.ceil(sortedData.length / itemsPerPage);
//   const currentData = sortedData.slice(
//     (currentPage - 1) * itemsPerPage,
//     currentPage * itemsPerPage
//   );

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//     if (tableRef.current) {
//       tableRef.current.scrollTop = 0;
//     }
//   };

//   const formatCurrency = (value) => {
//     return new Intl.NumberFormat("en-IN", {
//       style: "currency",
//       currency: "INR",
//     }).format(value);
//   };

//   const getUniqueValues = (key) => {
//     // Map the header names to the actual property names
//     const propertyMap = {
//       'Area': 'area',
//       'Unit': 'areaUnit'
//     };
//     const actualKey = propertyMap[key] || key;
    
//     const values = projectList.map((item) => item[actualKey]);
//     const uniqueValues = [...new Set(values)].filter((value) => {
//       if (value === undefined || value === null) return false;
//       if (typeof value === "number") {
//         return value.toString().includes(searchQuery);
//       }
//       return String(value).toLowerCase().includes(searchQuery.toLowerCase());
//     });
//     return uniqueValues.slice(0, 4);
//   };

//   const getShortName = (value) => {
//     if (!value) return "";
//     if (value.length > 8) return `${value.substring(0, 8)}...`;
//     if (value === "India") return "IN";
//     if (value === "United States") return "US";
//     if (value === "United Kingdom") return "UK";
//     if (value === "Maharashtra") return "MH";
//     if (value === "California") return "CA";
//     if (value === "New York") return "NY";
//     if (value === "Mumbai") return "MUM";
//     if (value === "Los Angeles") return "LA";
//     return value;
//   };

//   const truncateText = (text) => {
//     if (!text) return "";
//     return text.length > 8 ? `${text.substring(0, 8)}...` : text;
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return ""; 
//     const date = new Date(dateString);
//     return date.toLocaleDateString("en-GB", { day: '2-digit', month: '2-digit', year: '2-digit' });
//   };

//   const renderPaginationButtons = () => {
//     const buttons = [];
//     const maxVisibleButtons = 5;
    
//     buttons.push(
//       <button
//         key={1}
//         onClick={() => handlePageChange(1)}
//         className={`px-2 py-1 rounded text-xs font-medium transition-all border ${
//           currentPage === 1
//             ? "bg-blue-500 text-white border-blue-600"
//             : "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
//         }`}
//       >
//         1
//       </button>
//     );

//     let startPage = Math.max(2, currentPage - 1);
//     let endPage = Math.min(totalPages - 1, currentPage + 1);

//     if (currentPage <= 3) {
//       endPage = Math.min(4, totalPages - 1);
//     }
//     else if (currentPage >= totalPages - 2) {
//       startPage = Math.max(totalPages - 3, 2);
//     }

//     if (startPage > 2) {
//       buttons.push(
//         <span key="start-ellipsis" className="px-1 py-1 text-gray-400">
//           ...
//         </span>
//       );
//     }

//     for (let i = startPage; i <= endPage; i++) {
//       buttons.push(
//         <button
//           key={i}
//           onClick={() => handlePageChange(i)}
//           className={`px-2 py-1 rounded text-xs font-medium transition-all border ${
//             currentPage === i
//               ? "bg-blue-500 text-white border-blue-600"
//               : "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
//           }`}
//         >
//           {i}
//         </button>
//       );
//     }

//     if (endPage < totalPages - 1) {
//       buttons.push(
//         <span key="end-ellipsis" className="px-1 py-1 text-gray-400">
//           ...
//         </span>
//       );
//     }

//     if (totalPages > 1) {
//       buttons.push(
//         <button
//           key={totalPages}
//           onClick={() => handlePageChange(totalPages)}
//           className={`px-2 py-1 rounded text-xs font-medium transition-all border ${
//             currentPage === totalPages
//               ? "bg-blue-500 text-white border-blue-600"
//               : "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
//           }`}
//         >
//           {totalPages}
//         </button>
//       );
//     }    

//     return buttons;
//   };

//   return (
//     <div className="relative w-full h-[400px] bg-gray-900 shadow-md rounded-lg border border-gray-700 flex flex-col" >
//       {showDeleteModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div 
//             ref={modalRef}
//             className="bg-gray-800 rounded-lg p-6 w-full max-w-md transform transition-all border border-gray-700"
//           >
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold text-white">Confirm Deletion</h3>
//               <button 
//                 onClick={cancelDelete}
//                 className="text-gray-400 hover:text-white"
//               >
//                 <X className="w-6 h-6" />
//               </button>
//             </div>
//             <p className="text-gray-300 mb-6">
//               Are you sure you want to delete project "{projectToDelete?.project_name}"? This action cannot be undone.
//             </p>
//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={cancelDelete}
//                 className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition-colors border border-gray-500"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={confirmDelete}
//                 className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center border border-red-700"
//                 disabled={isLoading}
//               >
//                 {isLoading ? (
//                   <>
//                     <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                       <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                       <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                     </svg>
//                     Deleting...
//                   </>
//                 ) : (
//                   <>
//                     <Trash2 className="w-4 h-4 mr-2" />
//                     Delete
//                   </>
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {Object.keys(filters).length > 0 && (
//         <div className="p-2 bg-gray-800 border-b border-gray-700 flex flex-wrap items-center gap-2 sticky top-0 z-10">
//           <span className="text-xs text-gray-300 mr-1">Filters:</span>
//           {Object.entries(filters).map(([key, value]) => (
//             <div 
//               key={key} 
//               className="flex items-center bg-gray-700 rounded px-2 py-0.5 text-xs text-white border border-gray-600"
//             >
//               <span className="capitalize mr-1">{key.replace(/_/g, " ")}:</span>
//               <span>{truncateText(value)}</span>
//               <button 
//                 onClick={() => removeFilter(key)}
//                 className="ml-1 text-gray-400 hover:text-white"
//               >
//                 <X className="w-3 h-3" />
//               </button>
//             </div>
//           ))}
//           <button 
//             onClick={resetFilters}
//             className="ml-1 text-xs text-blue-400 hover:text-blue-300 border border-blue-500 rounded px-1.5 py-0.5"
//           >
//             Clear all
//           </button>
//         </div>
//       )}

//       <div className="flex-1 overflow-hidden">
//         <div className="w-full h-full overflow-x-auto">
//           <div className="w-[100%]">
//             <table className="min-w-max w-full border-separate border-spacing-0">
//               <thead className="bg-gray-900 text-xs font-semibold text-white sticky top-0 z-10" data-aos='fade-down'  data-aos-delay="300">
//                 <tr>
//                   {[
//                     "project_name",
//                     "sector",
//                     "Area",
//                     "Unit",
//                     "cost",
//                     "status",
//                     "country",
//                     "state",
//                     "city",
//                     "startDate",
//                     "endDate",
//                     "contractor",
//                     "action",
//                   ].map((header) => (
//                     <th 
//                       key={header} 
//                       className="p-2 text-left border-b border-t border-l border-gray-700 first:rounded-tl-lg last:rounded-tr-lg last:border-r relative whitespace-nowrap"
//                     >
//                       <div className="flex items-center">
//                         <span className="uppercase mr-1">{header.replace(/_/g, " ")}</span>
//                         {header !== "action" && (
//                           <div className="flex items-center space-x-0.5">
//                             <button
//                               onClick={() => setFilterDropdown(header === filterDropdown ? null : header)}
//                               className="p-0.5 hover:bg-gray-800 rounded"
//                             >
//                               <Filter className="w-3 h-3 text-gray-500" />
//                             </button>
//                             {header === "cost" && (
//                               <button
//                                 onClick={() => handleSort(header)}
//                                 className="p-0.5 hover:bg-gray-800 rounded"
//                               >
//                                 <ArrowUpDown className="w-3 h-3 text-gray-500" />
//                               </button>
//                             )}
//                           </div>
//                         )}
//                       </div>

//                       {filterDropdown === header && (
//                         <div 
//                           ref={dropdownRef} 
//                           className="absolute left-0 mt-1 bg-gray-800 text-white border border-gray-700 rounded-md shadow-lg z-20 w-40 max-h-60 overflow-hidden"
//                           style={{ top: "100%" }}
//                         >
//                           <div className="p-1 border-b border-gray-700">
//                             <div className="relative">
//                               <Search className="absolute left-2 top-2 h-3 w-3 text-gray-400" />
//                               <input
//                                 type="text"
//                                 placeholder="Search..."
//                                 className="w-full pl-7 pr-1 py-1 bg-gray-700 border border-gray-600 rounded text-xs text-white mb-0.5"
//                                 value={searchQuery}
//                                 onChange={(e) => setSearchQuery(e.target.value)}
//                               />
//                             </div>
//                           </div>
//                           <div className="overflow-y-auto max-h-48">
//                             {getUniqueValues(header).length > 0 ? (
//                               getUniqueValues(header).map((value) => (
//                                 <div
//                                   key={value}
//                                   className="px-2 py-1 text-xs hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0 truncate"
//                                   onClick={() => handleFilter(header, value)}
//                                   title={value}
//                                 >
//                                   {value}
//                                 </div>
//                               ))
//                             ) : (
//                               <div className="px-2 py-1 text-xs text-gray-400 text-center">
//                                 No matches
//                               </div>
//                             )}
//                           </div>
//                         </div>
//                       )}
//                     </th>
//                   ))}
//                 </tr>
//               </thead>

//               <tbody ref={tableRef}>
//                 {currentData.length > 0 ? (
//                   currentData.map((item, rowIndex) => (
//                     <tr
//                       key={item._id}
//                       className="hover:bg-gray-800 cursor-pointer transition-all"
//                       onClick={() => handleProjectClick(item)} data-aos="fade-up"  data-aos-delay="700"
//                     >
//                       {[
//                         <span title={item.project_name} className="block py-1">{truncateText(item.project_name)}</span>,
//                         <span title={item.sector} className="block py-1">{truncateText(item.sector)}</span>,
//                         truncateText(item.area || "-"),
//                         truncateText(item.areaUnit || "-"),
//                         formatCurrency(item.cost || "-"),
//                         truncateText(item.status || "-"),
//                         <span title={item.country}>{getShortName(item.country) || "-"}</span>,
//                         <span title={item.state}>{getShortName(item.state) || "-"}</span>,
//                         <span title={item.city}>{getShortName(item.city) || "-"}</span>,
//                         formatDate(item.startDate || "-"),
//                         formatDate(item.endDate || "-"),
//                         <span title={item.contractor}>{truncateText(item.contractor || "-")}</span>,
//                         <div key="action" className="flex space-x-3 items-start justify-start py-1">
//                           <button
//                             onClick={(e) => handleEditClick(item, e)}
//                             className="text-blue-500 hover:text-blue-700"
//                           >
//                             <Edit className="w-3 h-3" />
//                           </button>
//                           <button
//                             className="text-red-500 hover:text-red-700"
//                             onClick={(e) => handleDeleteClick(e, item)}
//                           >
//                             <Trash2 className="w-3 h-3" />
//                           </button>
//                         </div>,
//                       ].map((value, colIndex) => (
//                         <td 
//                           key={colIndex} 
//                           className={`p-0 text-xs text-white border-b border-l border-gray-700 
//                             ${colIndex === 0 ? "border-l-0" : ""}
//                             ${colIndex === 10 ? "border-r border-r-gray-700" : ""}
//                             ${rowIndex === currentData.length - 1 ? "border-b" : ""}
//                             ${rowIndex === currentData.length - 1 && colIndex === 0 ? "rounded-bl-lg" : ""}
//                             ${rowIndex === currentData.length - 1 && colIndex === 10 ? "rounded-br-lg" : ""}
//                           `}
//                         >
//                           <div className="p-2">{value}</div>
//                         </td>
//                       ))}
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td 
//                       colSpan={13} 
//                       className="text-center py-3 text-gray-500 border border-gray-700 text-xs"
//                     >
//                       {Object.keys(filters).length > 0 ? "No matching projects found" : "No data available"}
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </div>
//       </div>

//       <div className="flex justify-between items-center py-2 px-3 bg-gray-900 border-t border-gray-700 rounded-b-lg">
//         <div className="text-xs text-gray-400">
//           Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, sortedData.length)} of {sortedData.length} entries
//         </div>
//         <div className="flex gap-1 items-center">
//           <button
//             onClick={() => handlePageChange(currentPage - 1)}
//             className="px-2 py-1 rounded bg-gray-700 text-white flex items-center disabled:opacity-50 border border-gray-600 text-xs"
//             disabled={currentPage === 1}
//           >
//             <ChevronLeft className="w-3 h-3 mr-0.5" /> Prev
//           </button>

//           <div className="flex space-x-0.5">
//             {renderPaginationButtons()}
//           </div>

//           <button
//             onClick={() => handlePageChange(currentPage + 1)}
//             className="px-2 py-1 rounded bg-gray-700 text-white flex items-center disabled:opacity-50 border border-gray-600 text-xs"
//             disabled={currentPage === totalPages || totalPages === 0}
//           >
//             Next <ChevronRight className="w-3 h-3 ml-0.5" />
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ProjectList;



// import React, { useState, useEffect, useRef } from "react";
// import axios from "axios";
// import { ChevronLeft, ChevronRight, Filter, X, Search, Edit, Trash2, ArrowUpDown } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { deleteProject } from "../../FeatureRedux/project/deleteProject";
// import axiosInstance from "../axiosInstance";

// const ProjectList = ({ onEditProject, reload }) => {
//   const [projectList, setProjectList] = useState([]);
//   const [filters, setFilters] = useState({});
//   const [currentPage, setCurrentPage] = useState(1);
//   const [filterDropdown, setFilterDropdown] = useState(null);
//   const [searchQuery, setSearchQuery] = useState(""); 
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [projectToDelete, setProjectToDelete] = useState(null);
//   const itemsPerPage = 10;  
//   const navigate = useNavigate();
//   const dropdownRef = useRef(null);
//   const modalRef = useRef(null);
//   const tableRef = useRef(null);

//   const dispatch = useDispatch();
//   const { isLoading, isError, errorMessage, issuscess } = useSelector((state) => state.deleteProject);

//   useEffect(() => {
//     const fetchProjects = async () => {
//       try {
//         const response = await axiosInstance.get('/project/allProjects', {
//           headers: {
//             'x-company-id': '67ce66294f28f85e4ff91e2c'
//           }
//         });
//         setProjectList(response.data.projects);
//       } catch (error) {
//         console.error('Error fetching projects:', error.response?.data || error.message);
//       }
//     };
//     fetchProjects();
//   }, [reload, issuscess]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setFilterDropdown(null);
//       }
//       if (modalRef.current && !modalRef.current.contains(event.target)) {
//         setShowDeleteModal(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   const handleProjectClick = (project) => {
//     navigate('/detail', { state: { project } });
//   };

//   const handleEditClick = (project, e) => {
//     e.stopPropagation();
//     onEditProject(project);
//   };

//   const handleDeleteClick = (e, project) => {
//     e.stopPropagation();
//     setProjectToDelete(project);
//     setShowDeleteModal(true);
//   };

//   const confirmDelete = () => {
//     if (projectToDelete) {
//       dispatch(deleteProject(projectToDelete._id));
//       setShowDeleteModal(false);
//       setProjectToDelete(null);
//     }
//   };

//   const cancelDelete = () => {
//     setShowDeleteModal(false);
//     setProjectToDelete(null);
//   };

//   const handleFilter = (key, value) => {
//     setFilters((prevFilters) => ({
//       ...prevFilters,
//       [key]: value,
//     }));
//     setFilterDropdown(null);
//     setCurrentPage(1);
//   };

//   const removeFilter = (key) => {
//     setFilters((prevFilters) => {
//       const newFilters = { ...prevFilters };
//       delete newFilters[key];
//       return newFilters;
//     });
//     setCurrentPage(1);
//   };

//   const resetFilters = () => {
//     setFilters({});
//     setCurrentPage(1);
//   };

//   const handleSort = (key) => {
//     let direction = "asc";
//     if (sortConfig.key === key && sortConfig.direction === "asc") {
//       direction = "desc";
//     }
//     setSortConfig({ key, direction });
//   };

//   const filteredData = projectList.filter((item) => {
//     return Object.keys(filters).every((key) => {
//       if (!filters[key]) return true;
//       const itemValue = item[key];
//       const filterValue = filters[key];
      
//       if (itemValue == null || filterValue == null) return false;
      
//       if (typeof itemValue === "number") {
//         return itemValue === parseFloat(filterValue);
//       }
      
//       const itemStr = String(itemValue).toLowerCase();
//       const filterStr = String(filterValue).toLowerCase();
      
//       return itemStr.includes(filterStr);
//     });
//   });

//   const sortedData = [...filteredData].sort((a, b) => {
//     if (sortConfig.key) {
//       const aValue = a[sortConfig.key];
//       const bValue = b[sortConfig.key];
//       if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
//       if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
//     }
//     return 0;
//   });

//   const totalPages = Math.ceil(sortedData.length / itemsPerPage);
//   const currentData = sortedData.slice(
//     (currentPage - 1) * itemsPerPage,
//     currentPage * itemsPerPage
//   );

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//     if (tableRef.current) {
//       tableRef.current.scrollTop = 0;
//     }
//   };

//   const formatCurrency = (value) => {
//     return new Intl.NumberFormat("en-IN", {
//       style: "currency",
//       currency: "INR",
//     }).format(value);
//   };

//   const getUniqueValues = (key) => {
//     const values = projectList.map((item) => item[key]);
//     const uniqueValues = [...new Set(values)].filter((value) => {
//       if (value === undefined || value === null) return false;
//       if (typeof value === "number") {
//         return value.toString().includes(searchQuery);
//       }
//       return String(value).toLowerCase().includes(searchQuery.toLowerCase());
//     });
//     return uniqueValues.slice(0, 4);
//   };

//   const getShortName = (value) => {
//     if (!value) return "";
//     if (value.length > 8) return `${value.substring(0, 8)}...`;
//     if (value === "India") return "IN";
//     if (value === "United States") return "US";
//     if (value === "United Kingdom") return "UK";
//     if (value === "Maharashtra") return "MH";
//     if (value === "California") return "CA";
//     if (value === "New York") return "NY";
//     if (value === "Mumbai") return "MUM";
//     if (value === "Los Angeles") return "LA";
//     return value;
//   };

//   const truncateText = (text) => {
//     if (!text) return "";
//     return text.length > 8 ? `${text.substring(0, 8)}...` : text;
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return ""; 
//     const date = new Date(dateString);
//     return date.toLocaleDateString("en-GB", { day: '2-digit', month: '2-digit', year: '2-digit' });
//   };

//   const renderPaginationButtons = () => {
//     const buttons = [];
//     const maxVisibleButtons = 5;
    
//     buttons.push(
//       <button
//         key={1}
//         onClick={() => handlePageChange(1)}
//         className={`px-2 py-1 rounded text-xs font-medium transition-all border ${
//           currentPage === 1
//             ? "bg-blue-500 text-white border-blue-600"
//             : "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
//         }`}
//       >
//         1
//       </button>
//     );

//     let startPage = Math.max(2, currentPage - 1);
//     let endPage = Math.min(totalPages - 1, currentPage + 1);

//     if (currentPage <= 3) {
//       endPage = Math.min(4, totalPages - 1);
//     }
//     else if (currentPage >= totalPages - 2) {
//       startPage = Math.max(totalPages - 3, 2);
//     }

//     if (startPage > 2) {
//       buttons.push(
//         <span key="start-ellipsis" className="px-1 py-1 text-gray-400">
//           ...
//         </span>
//       );
//     }

//     for (let i = startPage; i <= endPage; i++) {
//       buttons.push(
//         <button
//           key={i}
//           onClick={() => handlePageChange(i)}
//           className={`px-2 py-1 rounded text-xs font-medium transition-all border ${
//             currentPage === i
//               ? "bg-blue-500 text-white border-blue-600"
//               : "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
//           }`}
//         >
//           {i}
//         </button>
//       );
//     }

//     if (endPage < totalPages - 1) {
//       buttons.push(
//         <span key="end-ellipsis" className="px-1 py-1 text-gray-400">
//           ...
//         </span>
//       );
//     }

//     if (totalPages > 1) {
//       buttons.push(
//         <button
//           key={totalPages}
//           onClick={() => handlePageChange(totalPages)}
//           className={`px-2 py-1 rounded text-xs font-medium transition-all border ${
//             currentPage === totalPages
//               ? "bg-blue-500 text-white border-blue-600"
//               : "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
//           }`}
//         >
//           {totalPages}
//         </button>
//       );
//     }    

//     return buttons;
//   };

//   return (
//     <div className="relative w-full h-[400px] bg-gray-900 shadow-md rounded-lg border border-gray-700 flex flex-col">
//       {showDeleteModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div 
//             ref={modalRef}
//             className="bg-gray-800 rounded-lg p-6 w-full max-w-md transform transition-all border border-gray-700"
//           >
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold text-white">Confirm Deletion</h3>
//               <button 
//                 onClick={cancelDelete}
//                 className="text-gray-400 hover:text-white"
//               >
//                 <X className="w-6 h-6" />
//               </button>
//             </div>
//             <p className="text-gray-300 mb-6">
//               Are you sure you want to delete project "{projectToDelete?.project_name}"? This action cannot be undone.
//             </p>
//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={cancelDelete}
//                 className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition-colors border border-gray-500"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={confirmDelete}
//                 className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center border border-red-700"
//                 disabled={isLoading}
//               >
//                 {isLoading ? (
//                   <>
//                     <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                       <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                       <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                     </svg>
//                     Deleting...
//                   </>
//                 ) : (
//                   <>
//                     <Trash2 className="w-4 h-4 mr-2" />
//                     Delete
//                   </>
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {Object.keys(filters).length > 0 && (
//         <div className="p-2 bg-gray-800 border-b border-gray-700 flex flex-wrap items-center gap-2 sticky top-0 z-10">
//           <span className="text-xs text-gray-300 mr-1">Filters:</span>
//           {Object.entries(filters).map(([key, value]) => (
//             <div 
//               key={key} 
//               className="flex items-center bg-gray-700 rounded px-2 py-0.5 text-xs text-white border border-gray-600"
//             >
//               <span className="capitalize mr-1">{key.replace(/_/g, " ")}:</span>
//               <span>{truncateText(value)}</span>
//               <button 
//                 onClick={() => removeFilter(key)}
//                 className="ml-1 text-gray-400 hover:text-white"
//               >
//                 <X className="w-3 h-3" />
//               </button>
//             </div>
//           ))}
//           <button 
//             onClick={resetFilters}
//             className="ml-1 text-xs text-blue-400 hover:text-blue-300 border border-blue-500 rounded px-1.5 py-0.5"
//           >
//             Clear all
//           </button>
//         </div>
//       )}

//       <div className="flex-1 overflow-hidden">
//         <div className="w-full h-full overflow-x-auto">
//           <div className="w-[100%]">
//             <table className="min-w-max w-full border-separate border-spacing-0">
//               <thead className="bg-gray-900 text-xs font-semibold text-white sticky top-0 z-10">
//                 <tr>
//                   {[
//                     "project_name",
//                     "sector",
//                     "Area",
//                     "Unit",
//                     "cost",
//                     "status",
//                     "country",
//                     "state",
//                     "city",
//                     "startDate",
//                     "endDate",
//                     "contractor",
//                     "action",
//                   ].map((header) => (
//                     <th 
//                       key={header} 
//                       className="p-2 text-left border-b border-t border-l border-gray-700 first:rounded-tl-lg last:rounded-tr-lg last:border-r relative whitespace-nowrap"
//                     >
//                       <div className="flex items-center">
//                         <span className="uppercase mr-1">{header.replace(/_/g, " ")}</span>
//                         {header !== "action" && (
//                           <div className="flex items-center space-x-0.5">
//                             <button
//                               onClick={() => setFilterDropdown(header === filterDropdown ? null : header)}
//                               className="p-0.5 hover:bg-gray-800 rounded"
//                             >
//                               <Filter className="w-3 h-3 text-gray-500" />
//                             </button>
//                             {header === "cost" && (
//                               <button
//                                 onClick={() => handleSort(header)}
//                                 className="p-0.5 hover:bg-gray-800 rounded"
//                               >
//                                 <ArrowUpDown className="w-3 h-3 text-gray-500" />
//                               </button>
//                             )}
//                           </div>
//                         )}
//                       </div>

//                       {filterDropdown === header && (
//                         <div 
//                           ref={dropdownRef} 
//                           className="absolute left-0 mt-1 bg-gray-800 text-white border border-gray-700 rounded-md shadow-lg z-20 w-40 max-h-60 overflow-hidden"
//                           style={{ top: "100%" }}
//                         >
//                           <div className="p-1 border-b border-gray-700">
//                             <div className="relative">
//                               <Search className="absolute left-2 top-2 h-3 w-3 text-gray-400" />
//                               <input
//                                 type="text"
//                                 placeholder="Search..."
//                                 className="w-full pl-7 pr-1 py-1 bg-gray-700 border border-gray-600 rounded text-xs text-white mb-0.5"
//                                 value={searchQuery}
//                                 onChange={(e) => setSearchQuery(e.target.value)}
//                               />
//                             </div>
//                           </div>
//                           <div className="overflow-y-auto max-h-48">
//                             {getUniqueValues(header).length > 0 ? (
//                               getUniqueValues(header).map((value) => (
//                                 <div
//                                   key={value}
//                                   className="px-2 py-1 text-xs hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0 truncate"
//                                   onClick={() => handleFilter(header, value)}
//                                   title={value}
//                                 >
//                                   {value}
//                                 </div>
//                               ))
//                             ) : (
//                               <div className="px-2 py-1 text-xs text-gray-400 text-center">
//                                 No matches
//                               </div>
//                             )}
//                           </div>
//                         </div>
//                       )}
//                     </th>
//                   ))}
//                 </tr>
//               </thead>

//               <tbody ref={tableRef}>
//                 {currentData.length > 0 ? (
//                   currentData.map((item, rowIndex) => (
//                     <tr
//                       key={item._id}
//                       className="hover:bg-gray-800 cursor-pointer transition-all"
//                       onClick={() => handleProjectClick(item)}
//                     >
//                       {[
//                         <span title={item.project_name} className="block py-1">{truncateText(item.project_name)}</span>,
//                        ,
//                         <span title={item.sector} className="block py-1">{truncateText(item.sector)}</span>,
//                         truncateText(item.area || "-"),
//                         truncateText(item.areaUnit || "-"),
//                         formatCurrency(item.cost || "-"),
//                         truncateText(item.status || "-"),
//                         <span title={item.country}>{getShortName(item.country) || "-"}</span>,
//                         <span title={item.state}>{getShortName(item.state) || "-"}</span>,
//                         <span title={item.city}>{getShortName(item.city) || "-"}</span>,
//                         formatDate(item.startDate || "-"),
//                         formatDate(item.endDate || "-"),
//                         <span title={item.contractor}>{truncateText(item.contractor || "-")}</span>,
//                         <div key="action" className="flex space-x-1 items-start justify-start py-1">
//                           <button
//                             onClick={(e) => handleEditClick(item, e)}
//                             className="text-blue-500 hover:text-blue-700"
//                           >
//                             <Edit className="w-3 h-3" />
//                           </button>
//                           <button
//                             className="text-red-500 hover:text-red-700"
//                             onClick={(e) => handleDeleteClick(e, item)}
//                           >
//                             <Trash2 className="w-3 h-3" />
//                           </button>
//                         </div>,
//                       ].map((value, colIndex) => (
//                         <td 
//                           key={colIndex} 
//                           className={`p-0 text-xs text-white border-b border-l border-gray-700 
//                             ${colIndex === 0 ? "border-l-0" : ""}
//                             ${colIndex === 10 ? "border-r border-r-gray-700" : ""}
//                             ${rowIndex === currentData.length - 1 ? "border-b" : ""}
//                             ${rowIndex === currentData.length - 1 && colIndex === 0 ? "rounded-bl-lg" : ""}
//                             ${rowIndex === currentData.length - 1 && colIndex === 10 ? "rounded-br-lg" : ""}
//                           `}
//                         >
//                           <div className="p-2">{value}</div>
//                         </td>
//                       ))}
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td 
//                       colSpan={13} 
//                       className="text-center py-3 text-gray-500 border border-gray-700 text-xs"
//                     >
//                       {Object.keys(filters).length > 0 ? "No matching projects found" : "No data available"}
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </div>
//       </div>

//       <div className="flex justify-between items-center py-2 px-3 bg-gray-900 border-t border-gray-700 rounded-b-lg">
//         <div className="text-xs text-gray-400">
//           Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, sortedData.length)} of {sortedData.length} entries
//         </div>
//         <div className="flex gap-1 items-center">
//           <button
//             onClick={() => handlePageChange(currentPage - 1)}
//             className="px-2 py-1 rounded bg-gray-700 text-white flex items-center disabled:opacity-50 border border-gray-600 text-xs"
//             disabled={currentPage === 1}
//           >
//             <ChevronLeft className="w-3 h-3 mr-0.5" /> Prev
//           </button>

//           <div className="flex space-x-0.5">
//             {renderPaginationButtons()}
//           </div>

//           <button
//             onClick={() => handlePageChange(currentPage + 1)}
//             className="px-2 py-1 rounded bg-gray-700 text-white flex items-center disabled:opacity-50 border border-gray-600 text-xs"
//             disabled={currentPage === totalPages || totalPages === 0}
//           >
//             Next <ChevronRight className="w-3 h-3 ml-0.5" />
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ProjectList;



// import React, { useState, useEffect, useRef } from "react";
// import axios from "axios";
// import { ChevronLeft, ChevronRight, Filter, X, Search, Edit, Trash2, ArrowUpDown } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { deleteProject } from "../../FeatureRedux/project/deleteProject";
// import axiosInstance from "../axiosInstance";

// const ProjectList = ({ onEditProject, reload }) => {
//   const [projectList, setProjectList] = useState([]);
//   const [filters, setFilters] = useState({});
//   const [currentPage, setCurrentPage] = useState(1);
//   const [filterDropdown, setFilterDropdown] = useState(null);
//   const [searchQuery, setSearchQuery] = useState(""); 
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [projectToDelete, setProjectToDelete] = useState(null);
//   const itemsPerPage = 10;  
//   const navigate = useNavigate();
//   const dropdownRef = useRef(null);
//   const modalRef = useRef(null);
//   const tableRef = useRef(null);

//   const dispatch = useDispatch();
//   const { isLoading, isError, errorMessage, issuscess } = useSelector((state) => state.deleteProject);

//   // Fetch projects from the backend
//   useEffect(() => {
//     const fetchProjects = async () => {
//       try {
//         const response = await axiosInstance.get('/project/allProjects', {
//           headers: {
//             'x-company-id': '67ce66294f28f85e4ff91e2c'
//           }
//         });

//         setProjectList(response.data.projects);
//       } catch (error) {
//         console.error('Error fetching projects:', error.response?.data || error.message);
//       }
//     };

//     fetchProjects();
//   }, [reload, issuscess]);

//   // Close dropdown/modal when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setFilterDropdown(null);
//       }
//       if (modalRef.current && !modalRef.current.contains(event.target)) {
//         setShowDeleteModal(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   // Handle project click to navigate to detail page
//   const handleProjectClick = (project) => {
//     navigate('/detail', { state: { project } });
//   };

//   // Handle edit icon click
//   const handleEditClick = (project, e) => {
//       e.stopPropagation();
//     onEditProject(project);
//   };

//   // Handle delete confirmation
//   const handleDeleteClick = (e, project) => {
//     e.stopPropagation();
//     setProjectToDelete(project);
//     setShowDeleteModal(true);
//   };

//   // Confirm delete action
//   const confirmDelete = () => {
//     if (projectToDelete) {
//       dispatch(deleteProject(projectToDelete._id));
//       setShowDeleteModal(false);
//       setProjectToDelete(null);
//     }
//   };

//   // Cancel delete action
//   const cancelDelete = () => {
//     setShowDeleteModal(false);
//     setProjectToDelete(null);
//   };

//   // Handle filtering
//   const handleFilter = (key, value) => {
//     setFilters((prevFilters) => ({
//       ...prevFilters,
//       [key]: value,
//     }));
//     setFilterDropdown(null);
//     setCurrentPage(1); // Reset to first page when applying new filter
//   };

//   // Remove a filter
//   const removeFilter = (key) => {
//     setFilters((prevFilters) => {
//       const newFilters = { ...prevFilters };
//       delete newFilters[key];
//       return newFilters;
//     });
//     setCurrentPage(1); // Reset to first page when removing filter
//   };

//   // Reset all filters
//   const resetFilters = () => {
//     setFilters({});
//     setCurrentPage(1); // Reset to first page when resetting filters
//   };

//   // Handle sorting
//   const handleSort = (key) => {
//     let direction = "asc";
//     if (sortConfig.key === key && sortConfig.direction === "asc") {
//       direction = "desc";
//     }
//     setSortConfig({ key, direction });
//   };

//   // Filtered and sorted data
//   const filteredData = projectList.filter((item) => {
//     return Object.keys(filters).every((key) => {
//       if (!filters[key]) return true;
//       const itemValue = item[key];
//       const filterValue = filters[key];
      
//       if (itemValue == null || filterValue == null) return false;
      
//       if (typeof itemValue === "number") {
//         return itemValue === parseFloat(filterValue);
//       }
      
//       const itemStr = String(itemValue).toLowerCase();
//       const filterStr = String(filterValue).toLowerCase();
      
//       return itemStr.includes(filterStr);
//     });
//   });

//   // Sort data
//   const sortedData = [...filteredData].sort((a, b) => {
//     if (sortConfig.key) {
//       const aValue = a[sortConfig.key];
//       const bValue = b[sortConfig.key];
//       if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
//       if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
//     }
//     return 0;
//   });

//   // Pagination logic
//   const totalPages = Math.ceil(sortedData.length / itemsPerPage);
//   const currentData = sortedData.slice(
//     (currentPage - 1) * itemsPerPage,
//     currentPage * itemsPerPage
//   );

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//     // Scroll to top of table when page changes
//     if (tableRef.current) {
//       tableRef.current.scrollTop = 0;
//     }
//   };

//   // Format currency
//   const formatCurrency = (value) => {
//     return new Intl.NumberFormat("en-IN", {
//       style: "currency",
//       currency: "INR",
//     }).format(value);
//   };

//   const getUniqueValues = (key) => {
//     const values = projectList.map((item) => item[key]);
//     const uniqueValues = [...new Set(values)].filter((value) => {
//       if (value === undefined || value === null) return false;
//       if (typeof value === "number") {
//         return value.toString().includes(searchQuery);
//       }
//       return String(value).toLowerCase().includes(searchQuery.toLowerCase());
//     });
//     return uniqueValues.slice(0, 4);
//   };

//   // Function to get short name for country, state, and city
//   const getShortName = (value) => {
//     if (!value) return "";
//     if (value.length > 12) {
//       return `${value.substring(0, 12)}...`;
//     }
//     if (value === "India") return "IN";
//     if (value === "United States") return "US";
//     if (value === "United Kingdom") return "UK";
//     if (value === "Maharashtra") return "MH";
//     if (value === "California") return "CA";
//     if (value === "New York") return "NY";
//     if (value === "Mumbai") return "MUM";
//     if (value === "Los Angeles") return "LA";
//     return value;
//   };

//   // Truncate long text to 12 characters
//   const truncateText = (text) => {
//     if (!text) return "";
//     return text.length > 12 ? `${text.substring(0, 12)}...` : text;
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return ""; 
//     const date = new Date(dateString);
//     return date.toLocaleDateString("en-GB");
//   };

//   // Generate pagination buttons
//   const renderPaginationButtons = () => {
//     const buttons = [];
//     const maxVisibleButtons = 5;
    
//     // Always show first page
//     buttons.push(
//       <button
//         key={1}
//         onClick={() => handlePageChange(1)}
//         className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all border ${
//           currentPage === 1
//             ? "bg-blue-500 text-white border-blue-600"
//             : "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
//         }`}
//       >
//         1
//       </button>
//     );

//     // Calculate range of pages to show
//     let startPage = Math.max(2, currentPage - 1);
//     let endPage = Math.min(totalPages - 1, currentPage + 1);

//     // Adjust if we're at the beginning
//     if (currentPage <= 3) {
//       endPage = Math.min(4, totalPages - 1);
//     }
//     // Adjust if we're at the end
//     else if (currentPage >= totalPages - 2) {
//       startPage = Math.max(totalPages - 3, 2);
//     }

//     // Add ellipsis if needed after first page
//     if (startPage > 2) {
//       buttons.push(
//         <span key="start-ellipsis" className="px-2 py-1.5 text-gray-400">
//           ...
//         </span>
//       );
//     }

//     // Add middle pages
//     for (let i = startPage; i <= endPage; i++) {
//       buttons.push(
//         <button
//           key={i}
//           onClick={() => handlePageChange(i)}
//           className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all border ${
//             currentPage === i
//               ? "bg-blue-500 text-white border-blue-600"
//               : "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
//           }`}
//         >
//           {i}
//         </button>
//       );
//     }

//     // Add ellipsis if needed before last page
//     if (endPage < totalPages - 1) {
//       buttons.push(
//         <span key="end-ellipsis" className="px-2 py-1.5 text-gray-400">
//           ...
//         </span>
//       );
//     }

//     // Always show last page if there's more than one page
//     if (totalPages > 1) {
//       buttons.push(
//         <button
//           key={totalPages}
//           onClick={() => handlePageChange(totalPages)}
//           className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all border ${
//             currentPage === totalPages
//               ? "bg-blue-500 text-white border-blue-600"
//               : "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
//           }`}
//         >
//           {totalPages}
//         </button>
//       );
//     }

//     return buttons;
//   };

//   return (
//     <div className="relative w-full h-[500px] bg-gray-900 shadow-md rounded-lg border border-gray-700 flex flex-col">
//       {/* Delete Confirmation Modal */}
//       {showDeleteModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div 
//             ref={modalRef}
//             className="bg-gray-800 rounded-lg p-6 w-full max-w-md transform transition-all border border-gray-700"
//           >
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold text-white">Confirm Deletion</h3>
//               <button 
//                 onClick={cancelDelete}
//                 className="text-gray-400 hover:text-white"
//               >
//                 <X className="w-6 h-6" />
//               </button>
//             </div>
//             <p className="text-gray-300 mb-6">
//               Are you sure you want to delete project "{projectToDelete?.project_name}"? This action cannot be undone.
//             </p>
//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={cancelDelete}
//                 className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition-colors border border-gray-500"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={confirmDelete}
//                 className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center border border-red-700"
//                 disabled={isLoading}
//               >
//                 {isLoading ? (
//                   <>
//                     <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                       <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                       <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                     </svg>
//                     Deleting...
//                   </>
//                 ) : (
//                   <>
//                     <Trash2 className="w-4 h-4 mr-2" />
//                     Delete
//                   </>
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Active Filters Section */}
//       {Object.keys(filters).length > 0 && (
//         <div className="p-3 bg-gray-800 border-b border-gray-700 flex flex-wrap items-center gap-2 sticky top-0 z-10">
//           <span className="text-sm text-gray-300 mr-2">Filters:</span>
//           {Object.entries(filters).map(([key, value]) => (
//             <div 
//               key={key} 
//               className="flex items-center bg-gray-700 rounded-md px-3 py-1 text-sm text-white border border-gray-600"
//             >
//               <span className="capitalize mr-1">{key.replace(/_/g, " ")}:</span>
//               <span>{value}</span>
//               <button 
//                 onClick={() => removeFilter(key)}
//                 className="ml-1 text-gray-400 hover:text-white"
//               >
//                 <X className="w-3 h-3" />
//               </button>
//             </div>
//           ))}
//           <button 
//             onClick={resetFilters}
//             className="ml-2 text-sm text-blue-400 hover:text-blue-300 border border-blue-500 rounded-md px-2 py-1"
//           >
//             Clear all
//           </button>
//         </div>
//       )}

//       {/* Table Container with Horizontal Scroll */}
//       <div className="flex-1 overflow-hidden">
//         <div className="w-full h-full overflow-x-auto">
//           <div className="w-[100%] h-full">
//             <table className="min-w-max w-full border-separate border-spacing-0">
//               {/* Table Header */}
//               <thead className="bg-gray-900 text-sm font-semibold text-white sticky top-0 z-10">
//                 <tr>
//                   {[
//                     "project_name",
//                     "sector",
//                     "Area",
//                     "Unit",
//                     "cost",
//                     "status",
//                     "country",
//                     "state",
//                     "city",
//                     "startDate",
//                     "endDate",
//                     "contractor",
//                     "action",
//                   ].map((header) => (
//                     <th 
//                       key={header} 
//                       className="p-3 text-left border-b border-t border-l border-gray-700 first:rounded-tl-lg last:rounded-tr-lg last:border-r relative whitespace-nowrap"
//                     >
//                       <div className="flex items-center">
//                         <span className="uppercase mr-2">{header.replace(/_/g, " ")}</span>
//                         {header !== "action" && (
//                           <div className="flex items-center space-x-1">
//                             <button
//                               onClick={(e) => {
//                                 e.stopPropagation();
//                                 setFilterDropdown(header === filterDropdown ? null : header);
//                               }}
//                               className="p-1 hover:bg-gray-800 rounded"
//                             >
//                               <Filter className="w-4 h-4 text-gray-500" />
//                             </button>
//                             {header === "cost" && (
//                               <button
//                                 onClick={() => handleSort(header)}
//                                 className="p-1 hover:bg-gray-800 rounded"
//                               >
//                                 <ArrowUpDown className="w-4 h-4 text-gray-500" />
//                               </button>
//                             )}
//                           </div>
//                         )}
//                       </div>

//                       {/* Filter Dropdown */}
//                       {filterDropdown === header && (
//                         <div 
//                           ref={dropdownRef} 
//                           className="absolute left-0 mt-2 bg-gray-800 text-white border border-gray-700 rounded-md shadow-lg z-50 w-40 max-h-60 overflow-auto"
//                           style={{ top: "calc(100% + 5px)" }}
//                         >
//                           <div className="p-2 border-b border-gray-700">
//                             <div className="relative">
//                               <Search className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
//                               <input
//                                 type="text"
//                                 placeholder="Search..."
//                                 className="w-full pl-8 pr-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm text-white mb-1"
//                                 value={searchQuery}
//                                 onChange={(e) => setSearchQuery(e.target.value)}
//                               />
//                             </div>
//                           </div>
//                           <div className="overflow-y-auto max-h-48">
//                             {getUniqueValues(header).length > 0 ? (
//                               getUniqueValues(header).map((value) => (
//                                 <div
//                                   key={value}
//                                   className="px-3 py-2 text-sm hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0 truncate"
//                                   onClick={() => handleFilter(header, value)}
//                                   title={value}
//                                 >
//                                   {value}
//                                 </div>
//                               ))
//                             ) : (
//                               <div className="px-3 py-2 text-sm text-gray-400 text-center">
//                                 No matches
//                               </div>
//                             )}
//                           </div>
//                         </div>
//                       )}
//                     </th>
//                   ))}
//                 </tr>
//               </thead>

//               {/* Table Body */}
//               <tbody ref={tableRef}>
//                 {currentData.length > 0 ? (
//                   currentData.map((item, rowIndex) => (
//                     <tr
//                       key={item._id}
//                       className="hover:bg-gray-800 cursor-pointer transition-all"
//                       onClick={() => handleProjectClick(item)}
//                     >
//                       {[
//                         <span title={item.project_name} className="block py-2">{truncateText(item.project_name)}</span>,
//                         truncateText(item.sector),
//                         truncateText(item.area || "-"),
//                         truncateText(item.areaUnit || "-"),
//                         formatCurrency(item.cost || "-"),
//                         truncateText(item.status || "-"),
//                         <span title={item.country}>{getShortName(item.country) || "-"}</span>,
//                         <span title={item.state}>{getShortName(item.state) || "-"}</span>,
//                         <span title={item.city}>{getShortName(item.city) || "-"}</span>,
//                         formatDate(item.startDate || "-"),
//                         formatDate(item.endDate || "-"),
//                         <span title={item.contractor}>{truncateText(item.contractor || "-")}</span>,
//                         <div key="action" className="flex space-x-2 items-center justify-center py-2">
//                           <button
//                             onClick={(e) => handleEditClick(item, e)}
//                             className="text-blue-500 hover:text-blue-700"
//                           >
//                             <Edit className="w-4 h-4" />
//                           </button>
//                           <button
//                             className="text-red-500 hover:text-red-700"
//                             onClick={(e) => handleDeleteClick(e, item)}
//                           >
//                             <Trash2 className="w-4 h-4" />
//                           </button>
//                         </div>,
//                       ].map((value, colIndex) => (
//                         <td 
//                           key={colIndex} 
//                           className={`p-0 text-sm text-white border-b border-l border-gray-700 
//                             ${colIndex === 0 ? "border-l-0" : ""}
//                             ${colIndex === 10 ? "border-r border-r-gray-700" : ""}
//                             ${rowIndex === currentData.length - 1 ? "border-b" : ""}
//                             ${rowIndex === currentData.length - 1 && colIndex === 0 ? "rounded-bl-lg" : ""}
//                             ${rowIndex === currentData.length - 1 && colIndex === 10 ? "rounded-br-lg" : ""}
//                           `}
//                         >
//                           <div className="p-3">{value}</div>
//                         </td>
//                       ))}
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td 
//                       colSpan={13} 
//                       className="text-center py-4 text-gray-500 border border-gray-700"
//                     >
//                       {Object.keys(filters).length > 0 ? "No matching projects found" : "No data available"}
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </div>
//       </div>

//       {/* Pagination */}
//       <div className="flex justify-between items-center py-3 px-4 bg-gray-900 border-t border-gray-700 rounded-b-lg">
//         <div className="text-sm text-gray-400">
//           Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, sortedData.length)} of {sortedData.length} entries
//         </div>
//         <div className="flex gap-2 items-center">
//           <button
//             onClick={() => handlePageChange(currentPage - 1)}
//             className="px-3 py-1.5 rounded-md bg-gray-700 text-white flex items-center disabled:opacity-50 border border-gray-600 text-sm"
//             disabled={currentPage === 1}
//           >
//             <ChevronLeft className="w-4 h-4 mr-1" /> Previous
//           </button>

//           <div className="flex space-x-1">
//             {renderPaginationButtons()}
//           </div>

//           <button
//             onClick={() => handlePageChange(currentPage + 1)}
//             className="px-3 py-1.5 rounded-md bg-gray-700 text-white flex items-center disabled:opacity-50 border border-gray-600 text-sm"
//             disabled={currentPage === totalPages || totalPages === 0}
//           >
//             Next <ChevronRight className="w-4 h-4 ml-1" />
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ProjectList;






// import React, { useState, useEffect, useRef } from "react";
// import axios from "axios";
// import { ChevronLeft, ChevronRight, Filter, X, Search, Edit, Trash2, ArrowUpDown } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { deleteProject } from "../../FeatureRedux/project/deleteProject";
// import axiosInstance from "../axiosInstance";

// const ProjectList = ({ onEditProject, reload }) => {
//   const [projectList, setProjectList] = useState([]);
//   const [filters, setFilters] = useState({});
//   const [currentPage, setCurrentPage] = useState(1);
//   const [filterDropdown, setFilterDropdown] = useState(null);
//   const [searchQuery, setSearchQuery] = useState(""); 
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [projectToDelete, setProjectToDelete] = useState(null);
//   const itemsPerPage = 10;
//   const navigate = useNavigate();
//   const dropdownRef = useRef(null);
//   const modalRef = useRef(null);
//   const tableRef = useRef(null);

//   const dispatch = useDispatch();
//   const { isLoading, isError, errorMessage, issuscess } = useSelector((state) => state.deleteProject);

//   // Fetch projects from the backend
//   useEffect(() => {
//     const fetchProjects = async () => {
//       try {
//         const response = await axiosInstance.get('/project/allProjects', {
//           headers: {
//             'x-company-id': '67ce66294f28f85e4ff91e2c'
//           }
//         });

//         setProjectList(response.data.projects);
//       } catch (error) {
//         console.error('Error fetching projects:', error.response?.data || error.message);
//       }
//     };

//     fetchProjects();
//   }, [reload, issuscess]);

//   // Close dropdown/modal when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setFilterDropdown(null);
//       }
//       if (modalRef.current && !modalRef.current.contains(event.target)) {
//         setShowDeleteModal(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   // Handle project click to navigate to detail page
//   const handleProjectClick = (project) => {
//     navigate('/detail', { state: { project } });
//   };

//   // Handle edit icon click
//   const handleEditClick = (project, e) => {
//       e.stopPropagation();
//     onEditProject(project);
//   };

//   // Handle delete confirmation
//   const handleDeleteClick = (e, project) => {
//     e.stopPropagation();
//     setProjectToDelete(project);
//     setShowDeleteModal(true);
//   };

//   // Confirm delete action
//   const confirmDelete = () => {
//     if (projectToDelete) {
//       dispatch(deleteProject(projectToDelete._id));
//       setShowDeleteModal(false);
//       setProjectToDelete(null);
//     }
//   };

//   // Cancel delete action
//   const cancelDelete = () => {
//     setShowDeleteModal(false);
//     setProjectToDelete(null);
//   };

//   // Handle filtering
//   const handleFilter = (key, value) => {
//     setFilters((prevFilters) => ({
//       ...prevFilters,
//       [key]: value,
//     }));
//     setFilterDropdown(null);
//     setCurrentPage(1); // Reset to first page when applying new filter
//   };

//   // Remove a filter
//   const removeFilter = (key) => {
//     setFilters((prevFilters) => {
//       const newFilters = { ...prevFilters };
//       delete newFilters[key];
//       return newFilters;
//     });
//     setCurrentPage(1); // Reset to first page when removing filter
//   };

//   // Reset all filters
//   const resetFilters = () => {
//     setFilters({});
//     setCurrentPage(1); // Reset to first page when resetting filters
//   };

//   // Handle sorting
//   const handleSort = (key) => {
//     let direction = "asc";
//     if (sortConfig.key === key && sortConfig.direction === "asc") {
//       direction = "desc";
//     }
//     setSortConfig({ key, direction });
//   };

//   // Filtered and sorted data
//   const filteredData = projectList.filter((item) => {
//     return Object.keys(filters).every((key) => {
//       if (!filters[key]) return true;
//       const itemValue = item[key];
//       const filterValue = filters[key];
      
//       if (itemValue == null || filterValue == null) return false;
      
//       if (typeof itemValue === "number") {
//         return itemValue === parseFloat(filterValue);
//       }
      
//       const itemStr = String(itemValue).toLowerCase();
//       const filterStr = String(filterValue).toLowerCase();
      
//       return itemStr.includes(filterStr);
//     });
//   });

//   // Sort data
//   const sortedData = [...filteredData].sort((a, b) => {
//     if (sortConfig.key) {
//       const aValue = a[sortConfig.key];
//       const bValue = b[sortConfig.key];
//       if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
//       if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
//     }
//     return 0;
//   });

//   // Pagination logic
//   const totalPages = Math.ceil(sortedData.length / itemsPerPage);
//   const currentData = sortedData.slice(
//     (currentPage - 1) * itemsPerPage,
//     currentPage * itemsPerPage
//   );

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//     // Scroll to top of table when page changes
//     if (tableRef.current) {
//       tableRef.current.scrollTop = 0;
//     }
//   };

//   // Format currency
//   const formatCurrency = (value) => {
//     return new Intl.NumberFormat("en-IN", {
//       style: "currency",
//       currency: "INR",
//     }).format(value);
//   };

//   const getUniqueValues = (key) => {
//     const values = projectList.map((item) => item[key]);
//     const uniqueValues = [...new Set(values)].filter((value) => {
//       if (value === undefined || value === null) return false;
//       if (typeof value === "number") {
//         return value.toString().includes(searchQuery);
//       }
//       return String(value).toLowerCase().includes(searchQuery.toLowerCase());
//     });
//     return uniqueValues.slice(0, 4);
//   };

//   // Function to get short name for country, state, and city
//   const getShortName = (value) => {
//     if (!value) return "";
//     if (value.length > 12) {
//       return `${value.substring(0, 12)}...`;
//     }
//     if (value === "India") return "IN";
//     if (value === "United States") return "US";
//     if (value === "United Kingdom") return "UK";
//     if (value === "Maharashtra") return "MH";
//     if (value === "California") return "CA";
//     if (value === "New York") return "NY";
//     if (value === "Mumbai") return "MUM";
//     if (value === "Los Angeles") return "LA";
//     return value;
//   };

//   // Truncate long text to 12 characters
//   const truncateText = (text) => {
//     if (!text) return "";
//     return text.length > 12 ? `${text.substring(0, 12)}...` : text;
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return ""; 
//     const date = new Date(dateString);
//     return date.toLocaleDateString("en-GB");
//   };

//   return (
//     <div className="relative w-full bg-gray-900 shadow-md rounded-lg border border-gray-700 flex flex-col">
//   {/* Delete Confirmation Modal */}
//   {showDeleteModal && (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//       <div 
//         ref={modalRef}
//         className="bg-gray-800 rounded-lg p-6 w-full max-w-md transform transition-all border border-gray-700"
//       >
//         <div className="flex justify-between items-center mb-4">
//           <h3 className="text-xl font-semibold text-white">Confirm Deletion</h3>
//           <button 
//             onClick={cancelDelete}
//             className="text-gray-400 hover:text-white"
//           >
//             <X className="w-6 h-6" />
//           </button>
//         </div>
//         <p className="text-gray-300 mb-6">
//           Are you sure you want to delete project "{projectToDelete?.project_name}"? This action cannot be undone.
//         </p>
//         <div className="flex justify-end space-x-4">
//           <button
//             onClick={cancelDelete}
//             className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition-colors border border-gray-500"
//           >
//             Cancel
//           </button>
//           <button
//             onClick={confirmDelete}
//             className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center border border-red-700"
//             disabled={isLoading}
//           >
//             {isLoading ? (
//               <>
//                 <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                   <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                   <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                 </svg>
//                 Deleting...
//               </>
//             ) : (
//               <>
//                 <Trash2 className="w-4 h-4 mr-2" />
//                 Delete
//               </>
//             )}
//           </button>
//         </div>
//       </div>
//     </div>
//   )}

//   {/* Active Filters Section */}
//   {Object.keys(filters).length > 0 && (
//     <div className="p-3 bg-gray-800 border-b border-gray-700 flex flex-wrap items-center gap-2 sticky top-0 z-10">
//       <span className="text-sm text-gray-300 mr-2">Filters:</span>
//       {Object.entries(filters).map(([key, value]) => (
//         <div 
//           key={key} 
//           className="flex items-center bg-gray-700 rounded-md px-3 py-1 text-sm text-white border border-gray-600"
//         >
//           <span className="capitalize mr-1">{key.replace(/_/g, " ")}:</span>
//           <span>{value}</span>
//           <button 
//             onClick={() => removeFilter(key)}
//             className="ml-1 text-gray-400 hover:text-white"
//           >
//             <X className="w-3 h-3" />
//           </button>
//         </div>
//       ))}
//       <button 
//         onClick={resetFilters}
//         className="ml-2 text-sm text-blue-400 hover:text-blue-300 border border-blue-500 rounded-md px-2 py-1"
//       >
//         Clear all
//       </button>
//     </div>
//   )}

//   {/* Table Container with Horizontal Scroll */}
//   <div className="flex-1 overflow-hidden">
//     <div className="w-full overflow-x-auto">
//       <div className="w-[100%]">
//         <table className="min-w-max w-full border-separate border-spacing-0">
//           {/* Table Header */}
//           <thead className="bg-gray-900 text-sm font-semibold text-white sticky top-0 z-10">
//             <tr>
//               {[
//                 "project_name",
//                 "sector",
//                 "Area",
//                 "Unit",
//                 "cost",
//                 "status",
//                 "country",
//                 "state",
//                 "city",
//                 "startDate",
//                 "endDate",
//                 "contractor",
//                 "action",
//               ].map((header) => (
//                 <th 
//                   key={header} 
//                   className="p-3 text-left border-b border-t border-l border-gray-700 first:rounded-tl-lg last:rounded-tr-lg last:border-r relative whitespace-nowrap"
//                 >
//                   <div className="flex items-center">
//                     <span className="uppercase mr-2">{header.replace(/_/g, " ")}</span>
//                     {header !== "action" && (
//                       <div className="flex items-center space-x-1">
//                         <button
//                           onClick={() => setFilterDropdown(header === filterDropdown ? null : header)}
//                           className="p-1 hover:bg-gray-800 rounded"
//                         >
//                           <Filter className="w-4 h-4 text-gray-500" />
//                         </button>
//                         {header === "cost" && (
//                           <button
//                             onClick={() => handleSort(header)}
//                             className="p-1 hover:bg-gray-800 rounded"
//                           >
//                             <ArrowUpDown className="w-4 h-4 text-gray-500" />
//                           </button>
//                         )}
//                       </div>
//                     )}
//                   </div>

//                   {/* Filter Dropdown */}
//                   {filterDropdown === header && (
//                     <div 
//                       ref={dropdownRef} 
//                       className="absolute left-0 mt-1 bg-gray-900 text-white border border-gray-700 rounded-md shadow-lg z-20 w-48"
//                       style={{ top: "100%" }}
//                     >
//                       <div className="p-2">
//                         <input
//                           type="text"
//                           placeholder="Search..."
//                           className="w-full p-1 border border-gray-300 rounded-md text-gray-900 mb-2"
//                           value={searchQuery}
//                           onChange={(e) => setSearchQuery(e.target.value)}
//                         />
//                         <div className="max-h-48 overflow-y-auto">
//                           {getUniqueValues(header).map((value) => (
//                             <div
//                               key={value}
//                               className="p-2 hover:bg-gray-800 cursor-pointer border-b border-gray-700 last:border-b-0"
//                               onClick={() => handleFilter(header, value)}
//                             >
//                               {value}
//                             </div>
//                           ))}
//                         </div>
//                       </div>
//                     </div>
//                   )}
//                 </th>
//               ))}
//             </tr>
//           </thead>

//           {/* Table Body */}
//           <tbody>
//             {currentData.length > 0 ? (
//               currentData.map((item, rowIndex) => (
//                 <tr
//                   key={item._id}
//                   className="hover:bg-gray-800 cursor-pointer transition-all"
//                   onClick={() => handleProjectClick(item)}
//                 >
//                   {[
//                     <span title={item.project_name} className="block py-2">{truncateText(item.project_name)}</span>,
//                     truncateText(item.sector),
//                     truncateText(item.area || "-"),
//                     truncateText(item.areaUnit || "-"),
//                     formatCurrency(item.cost || "-"),
//                     truncateText(item.status || "-"),
//                     <span title={item.country}>{getShortName(item.country) || "-"}</span>,
//                     <span title={item.state}>{getShortName(item.state) || "-"}</span>,
//                     <span title={item.city}>{getShortName(item.city) || "-"}</span>,
//                     formatDate(item.startDate || "-"),
//                     formatDate(item.endDate || "-"),
//                     <span title={item.contractor}>{truncateText(item.contractor || "-")}</span>,
//                     <div key="action" className="flex space-x-2 items-center justify-center py-2">
//                       <button
//                         onClick={(e) => handleEditClick(item, e)}
//                         className="text-blue-500 hover:text-blue-700"
//                       >
//                         <Edit className="w-4 h-4" />
//                       </button>
//                       <button
//                         className="text-red-500 hover:text-red-700"
//                         onClick={(e) => handleDeleteClick(e, item)}
//                       >
//                         <Trash2 className="w-4 h-4" />
//                       </button>
//                     </div>,
//                   ].map((value, colIndex) => (
//                     <td 
//                       key={colIndex} 
//                       className={`p-0 text-sm text-white border-b border-l border-gray-700 
//                         ${colIndex === 0 ? "border-l-0" : ""}
//                         ${colIndex === 10 ? "border-r border-r-gray-700" : ""}
//                         ${rowIndex === currentData.length - 1 ? "border-b" : ""}
//                         ${rowIndex === currentData.length - 1 && colIndex === 0 ? "rounded-bl-lg" : ""}
//                         ${rowIndex === currentData.length - 1 && colIndex === 10 ? "rounded-br-lg" : ""}
//                       `}
//                     >
//                       <div className="p-3">{value}</div>
//                     </td>
//                   ))}
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td 
//                   colSpan={13} 
//                   className="text-center py-4 text-gray-500 border border-gray-700"
//                 >
//                   {Object.keys(filters).length > 0 ? "No matching projects found" : "No data available"}
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   </div>

//   {/* Pagination */}
//   <div className="flex justify-between items-center py-3 px-4 bg-gray-900 border-t border-gray-700 rounded-b-lg">
//     <div className="text-sm text-gray-400">
//       Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, sortedData.length)} of {sortedData.length} entries
//     </div>
//     <div className="flex gap-2 items-center">
//       <button
//         onClick={() => handlePageChange(currentPage - 1)}
//         className="px-3 py-1.5 rounded-md bg-gray-700 text-white flex items-center disabled:opacity-50 border border-gray-600 text-sm"
//         disabled={currentPage === 1}
//       >
//         <ChevronLeft className="w-4 h-4 mr-1" /> Previous
//       </button>

//       <div className="flex space-x-1">
//         {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
//           let pageNum;
//           if (totalPages <= 5) {
//             pageNum = index + 1;
//           } else if (currentPage <= 3) {
//             pageNum = index + 1;
//           } else if (currentPage >= totalPages - 2) {
//             pageNum = totalPages - 4 + index;
//           } else {
//             pageNum = currentPage - 2 + index;
//           }

//           if (pageNum > totalPages) return null;

//           return (
//             <button
//               key={index}
//               onClick={() => handlePageChange(pageNum)}
//               className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all border ${
//                 currentPage === pageNum
//                   ? "bg-blue-500 text-white border-blue-600"
//                   : "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
//               }`}
//             >
//               {pageNum}
//             </button>
//           );
//         })}
//         {totalPages > 5 && currentPage < totalPages - 2 && (
//           <>
//             <span className="px-2 py-1.5 text-gray-400">...</span>
//             <button
//               onClick={() => handlePageChange(totalPages)}
//               className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all border ${
//                 currentPage === totalPages
//                   ? "bg-blue-500 text-white border-blue-600"
//                   : "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
//               }`}
//             >
//               {totalPages}
//             </button>
//           </>
//         )}
//       </div>

//       <button
//         onClick={() => handlePageChange(currentPage + 1)}
//         className="px-3 py-1.5 rounded-md bg-gray-700 text-white flex items-center disabled:opacity-50 border border-gray-600 text-sm"
//         disabled={currentPage === totalPages}
//       >
//         Next <ChevronRight className="w-4 h-4 ml-1" />
//       </button>
//     </div>
//   </div>
// </div>

//   );
// };

// export default ProjectList;   





// above code also working ........
// import React, { useState, useEffect, useRef } from "react";
// import axios from "axios";
// import { ChevronLeft, ChevronRight, Filter, X, Search, Edit, Trash2, ArrowUpDown } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { deleteProject } from "../../FeatureRedux/project/deleteProject";
// import axiosInstance from "../axiosInstance";

// const ProjectList = ({ onEditProject , reload }) => {
//   const [projectList, setProjectList] = useState([]);
//   const [filters, setFilters] = useState({});
//   const [currentPage, setCurrentPage] = useState(1);
//   const [filterDropdown, setFilterDropdown] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [projectToDelete, setProjectToDelete] = useState(null);
//   const itemsPerPage = 10;
//   const navigate = useNavigate();
//   const dropdownRef = useRef(null);
//   const modalRef = useRef(null);
//   const tableRef = useRef(null);

//   const dispatch = useDispatch();
//   const { isLoading, isError, errorMessage , issuscess } = useSelector((state) => state.deleteProject);

//   // Fetch projects from the backend
//   useEffect(() => {
//     const fetchProjects = async () => {
//       try {
//         const response = await axiosInstance.get('/project/allProjects', {
//           headers: {
//             'x-company-id': '67ce66294f28f85e4ff91e2c'
//           }
//         });
//         setProjectList(response.data.projects);
//       } catch (error) {
//         console.error('Error fetching projects:', error.response?.data || error.message);
//       }
//     };

//     fetchProjects();
//   }, [reload , issuscess]);

//   // Close dropdown/modal when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setFilterDropdown(null);
//       }
//       if (modalRef.current && !modalRef.current.contains(event.target)) {
//         setShowDeleteModal(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   // Handle project click to navigate to detail page
//   const handleProjectClick = (project) => {
//     navigate('/detail', { state: { project } });
//   };

//   // Handle edit icon click
//   const handleEditClick = (project, e) => {
//     e.stopPropagation();
//     onEditProject(project);
//   };

//   // Handle delete confirmation
//   const handleDeleteClick = (e, project) => {
//     e.stopPropagation();
//     setProjectToDelete(project);
//     setShowDeleteModal(true);
//   };

//   // Confirm delete action
//   const confirmDelete = () => {
//     if (projectToDelete) {
//       dispatch(deleteProject(projectToDelete._id));
//       setShowDeleteModal(false);
//       setProjectToDelete(null);
//     }
//   };

//   // Cancel delete action
//   const cancelDelete = () => {
//     setShowDeleteModal(false);
//     setProjectToDelete(null);
//   };

//   // Handle filtering
//   const handleFilter = (key, value) => {
//     setFilters((prevFilters) => ({
//       ...prevFilters,
//       [key]: value,
//     }));
//     setFilterDropdown(null);
//     setCurrentPage(1); // Reset to first page when applying new filter
//   };

//   // Remove a filter
//   const removeFilter = (key) => {
//     setFilters((prevFilters) => {
//       const newFilters = { ...prevFilters };
//       delete newFilters[key];
//       return newFilters;
//     });
//     setCurrentPage(1); // Reset to first page when removing filter
//   };

//   // Reset all filters
//   const resetFilters = () => {
//     setFilters({});
//     setCurrentPage(1); // Reset to first page when resetting filters
//   };

//   // Handle sorting
//   const handleSort = (key) => {
//     let direction = "asc";
//     if (sortConfig.key === key && sortConfig.direction === "asc") {
//       direction = "desc";
//     }
//     setSortConfig({ key, direction });
//   };

//   // Filtered and sorted data
//   const filteredData = projectList.filter((item) => {
//     return Object.keys(filters).every((key) => {
//       if (!filters[key]) return true;
//       const itemValue = item[key];
//       const filterValue = filters[key];
      
//       if (itemValue == null || filterValue == null) return false;
      
//       if (typeof itemValue === "number") {
//         return itemValue === parseFloat(filterValue);
//       }
      
//       const itemStr = String(itemValue).toLowerCase();
//       const filterStr = String(filterValue).toLowerCase();
      
//       return itemStr.includes(filterStr);
//     });
//   });

//   // Sort data
//   const sortedData = [...filteredData].sort((a, b) => {
//     if (sortConfig.key) {
//       const aValue = a[sortConfig.key];
//       const bValue = b[sortConfig.key];
//       if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
//       if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
//     }
//     return 0;
//   });

//   // Pagination logic
//   const totalPages = Math.ceil(sortedData.length / itemsPerPage);
//   const currentData = sortedData.slice(
//     (currentPage - 1) * itemsPerPage,
//     currentPage * itemsPerPage
//   );

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//   };

//   // Format currency
//   const formatCurrency = (value) => {
//     return new Intl.NumberFormat("en-IN", {
//       style: "currency",
//       currency: "INR",
//     }).format(value);
//   };

//   const getUniqueValues = (key) => {
//     const values = projectList.map((item) => item[key]);
//     const uniqueValues = [...new Set(values)].filter((value) => {
//       if (value === undefined || value === null) return false;
//       if (typeof value === "number") {
//         return value.toString().includes(searchQuery);
//       }
//       return String(value).toLowerCase().includes(searchQuery.toLowerCase());
//     });
//     return uniqueValues.slice(0, 4);
//   };

//   // Function to get short name for country, state, and city
//   const getShortName = (value) => {
//     if (!value) return "";
//     if (value.length > 12) {
//       return `${value.substring(0, 12)}...`;
//     }
//     if (value === "India") return "IN";
//     if (value === "United States") return "US";
//     if (value === "United Kingdom") return "UK";
//     if (value === "Maharashtra") return "MH";
//     if (value === "California") return "CA";
//     if (value === "New York") return "NY";
//     if (value === "Mumbai") return "MUM";
//     if (value === "Los Angeles") return "LA";
//     return value;
//   };

//   // Truncate long text to 12 characters
//   const truncateText = (text) => {
//     if (!text) return "";
//     return text.length > 12 ? `${text.substring(0, 12)}...` : text;
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return ""; 
//     const date = new Date(dateString);
//     return date.toLocaleDateString("en-GB");
//   };

//   return (
//     <div className="relative w-full h-[75vh] bg-gray-900 shadow-md rounded-lg border border-gray-700 flex flex-col">
//       {/* Delete Confirmation Modal */}
//       {showDeleteModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div 
//             ref={modalRef}
//             className="bg-gray-800 rounded-lg p-6 w-full max-w-md transform transition-all border border-gray-700"
//           >
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold text-white">Confirm Deletion</h3>
//               <button 
//                 onClick={cancelDelete}
//                 className="text-gray-400 hover:text-white"
//               >
//                 <X className="w-6 h-6" />
//               </button>
//             </div>
//             <p className="text-gray-300 mb-6">
//               Are you sure you want to delete project "{projectToDelete?.project_name}"? This action cannot be undone.
//             </p>
//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={cancelDelete}
//                 className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition-colors border border-gray-500"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={confirmDelete}
//                 className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center border border-red-700"
//                 disabled={isLoading}
//               >
//                 {isLoading ? (
//                   <>
//                     <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                       <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                       <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                     </svg>
//                     Deleting...
//                   </>
//                 ) : (
//                   <>
//                     <Trash2 className="w-4 h-4 mr-2" />
//                     Delete
//                   </>
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Active Filters Section */}
//       {Object.keys(filters).length > 0 && (
//         <div className="p-3 bg-gray-800 border-b border-gray-700 flex flex-wrap items-center gap-2 sticky top-0 z-10">
//           <span className="text-sm text-gray-300 mr-2">Filters:</span>
//           {Object.entries(filters).map(([key, value]) => (
//             <div 
//               key={key} 
//               className="flex items-center bg-gray-700 rounded-md px-3 py-1 text-sm text-white border border-gray-600"
//             >
//               <span className="capitalize mr-1">{key.replace(/_/g, " ")}:</span>
//               <span>{value}</span>
//               <button 
//                 onClick={() => removeFilter(key)}
//                 className="ml-1 text-gray-400 hover:text-white"
//               >
//                 <X className="w-3 h-3" />
//               </button>
//             </div>
//           ))}
//           <button 
//             onClick={resetFilters}
//             className="ml-2 text-sm text-blue-400 hover:text-blue-300 border border-blue-500 rounded-md px-2 py-1"
//           >
//             Clear all
//           </button>
//         </div>
//       )}

//       <div className="flex-1" ref={tableRef}>
//         <table className="w-full border-separate border-spacing-0">
//           {/* Table Header */}
//           <thead className="bg-gray-900 text-sm font-semibold text-white sticky top-0 z-10">
//             <tr>
//               {[
//                 "project_name",
//                 "sector",
//                 "Area",
//                 "Unit",
//                 "cost",
//                 "status",
//                 "country",
//                 "state",
//                 "city",
//                 "startDate",
//                 "endDate",
//                 "contractor",
//                 "action",
//               ].map((header) => (
//                 <th 
//                   key={header} 
//                   className="p-3 text-left border-b border-t border-l border-gray-700 first:rounded-tl-lg last:rounded-tr-lg last:border-r relative"
//                 >
//                   <div className="flex items-center">
//                     <span className="uppercase mr-2">{header.replace(/_/g, " ")}</span>
//                     {header !== "action" && (
//                       <div className="flex items-center space-x-1">
//                         <button
//                           onClick={() => setFilterDropdown(header === filterDropdown ? null : header)}
//                           className="p-1 hover:bg-gray-800 rounded"
//                         >
//                           <Filter className="w-4 h-4 text-gray-500" />
//                         </button>
//                         {header === "cost" && (
//                           <button
//                             onClick={() => handleSort(header)}
//                             className="p-1 hover:bg-gray-800 rounded"
//                           >
//                             <ArrowUpDown className="w-4 h-4 text-gray-500" />
//                           </button>
//                         )}
//                       </div>
//                     )}
//                   </div>
//                   {/* Filter Dropdown - now positioned absolutely below each header */}
//                   {filterDropdown === header && (
//                     <div 
//                       ref={dropdownRef} 
//                       className="absolute left-0 mt-1 bg-gray-900 text-white border border-gray-700 rounded-md shadow-lg z-20 w-48"
//                       style={{ top: "100%" }} // Position below the header
//                     >
//                       <div className="p-2">
//                         <input
//                           type="text"
//                           placeholder="Search..."
//                           className="w-full p-1 border border-gray-300 rounded-md text-gray-900 mb-2"
//                           value={searchQuery}
//                           onChange={(e) => setSearchQuery(e.target.value)}
//                         />
//                         <div className="max-h-48 overflow-y-auto">
//                           {getUniqueValues(header).map((value) => (
//                             <div
//                               key={value}
//                               className="p-2 hover:bg-gray-800 cursor-pointer border-b border-gray-700 last:border-b-0"
//                               onClick={() => handleFilter(header, value)}
//                             >
//                               {value}
//                             </div>
//                           ))}
//                         </div>
//                       </div>
//                     </div>
//                   )}
//                 </th>
//               ))}
//             </tr>
//           </thead>

//           {/* Table Body */}
//           <tbody>
//             {currentData.length > 0 ? (
//               currentData.map((item, rowIndex) => (
//                 <tr
//                   key={item._id}
//                   className="hover:bg-gray-800 cursor-pointer transition-all"
//                   onClick={() => handleProjectClick(item)}
//                 >
//                   {[
//                     <span title={item.project_name} className="block py-2">{truncateText(item.project_name)}</span>,
//                     truncateText(item.sector),
//                     truncateText(item.area || "-"),
//                     truncateText(item.areaUnit || "-"),
//                     formatCurrency(item.cost || "-"),
//                     truncateText(item.status || "-"),
//                     <span title={item.country}>{getShortName(item.country) || "-"}</span>,
//                     <span title={item.state}>{getShortName(item.state) || "-"}</span>,
//                     <span title={item.city}>{getShortName(item.city) || "-"}</span>,
//                     formatDate(item.startDate || "-"),
//                     formatDate(item.endDate || "-"),
//                     <span title={item.contractor}>{truncateText(item.contractor || "-")}</span>,
//                     <div key="action" className="flex space-x-2 items-center justify-center py-2">
//                       <button
//                         onClick={(e) => handleEditClick(item, e)}
//                         className="text-blue-500 hover:text-blue-700"
//                       >
//                         <Edit className="w-4 h-4" />
//                       </button>
//                       <button
//                         className="text-red-500 hover:text-red-700"
//                         onClick={(e) => handleDeleteClick(e, item)}
//                       >
//                         <Trash2 className="w-4 h-4" />
//                       </button>
//                     </div>,
//                   ].map((value, colIndex) => (
//                     <td 
//                       key={colIndex} 
//                       className={`p-0 text-sm text-white border-b border-l border-gray-700 
//                         ${colIndex === 0 ? "border-l-0" : ""}
//                         ${colIndex === 10 ? "border-r border-r-gray-700" : ""}
//                         ${rowIndex === currentData.length - 1 ? "border-b" : ""}
//                         ${rowIndex === currentData.length - 1 && colIndex === 0 ? "rounded-bl-lg" : ""}
//                         ${rowIndex === currentData.length - 1 && colIndex === 10 ? "rounded-br-lg" : ""}
//                       `}
//                     >
//                       <div className="p-3">
//                         {value}
//                       </div>
//                     </td>
//                   ))}
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td 
//                   colSpan={11} 
//                   className="text-center py-4 text-gray-500 border border-gray-700"
//                 >
//                   {Object.keys(filters).length > 0 ? "No matching projects found" : "No data available"}
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* Pagination */}
//       <div className="flex justify-end gap-2 py-4 items-center px-4 bg-gray-900 sticky bottom-0 border-t border-gray-700 rounded-b-lg">
//             <button
//               onClick={() => handlePageChange(currentPage - 1)}
//               className={`px-4 py-2 rounded-md bg-blue-500 text-white flex items-center disabled:opacity-50 border border-blue-600`}
//               disabled={currentPage === 1}
//             >
//               <ChevronLeft className="w-4 h-4 mr-1" /> Previous
//             </button>

//             <div className="flex space-x-2">
//               {Array.from({ length: totalPages }, (_, index) => (
//                 <button
//                   key={index}
//                   onClick={() => handlePageChange(index + 1)}
//                   className={`px-4 py-2 rounded-md text-sm font-semibold transition-all border ${
//                     currentPage === index + 1
//                       ? "bg-blue-500 text-white border-blue-600"
//                       : "bg-gray-200 text-gray-800 border-gray-300"
//                   }`}
//                 >
//                   {index + 1}
//                 </button>
//               ))}
//             </div>

//             <button
//               onClick={() => handlePageChange(currentPage + 1)}
//               className="px-4 py-2 rounded-md bg-blue-500 text-white flex items-center disabled:opacity-50 border border-blue-600"
//               disabled={currentPage === totalPages}
//             >
//               Next <ChevronRight className="w-4 h-4 ml-1" />
//             </button>
//           </div>

//     </div>
//   );
// };

// export default ProjectList;


// import React, { useState, useEffect, useRef } from "react";
// import axios from "axios";
// import { ChevronLeft, ChevronRight, Filter, X, Search, Edit, Trash2, ArrowUpDown } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { deleteProject } from "../../FeatureRedux/project/deleteProject";

// const ProjectList = ({ onEditProject }) => {
//   const [projectList, setProjectList] = useState([]);
//   const [filters, setFilters] = useState({});
//   const [currentPage, setCurrentPage] = useState(1);
//   const [filterDropdown, setFilterDropdown] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
//   const itemsPerPage = 10;
//   const navigate = useNavigate();
//   const dropdownRef = useRef(null);

//   const dispatch = useDispatch();
//   const { isLoading, isError, errorMessage } = useSelector((state) => state.deleteProject);

//   // Fetch projects from the backend
//   useEffect(() => {
//     const fetchProjects = async () => {
//       try {
//         const response = await axios.get('http://localhost:3002/api/project/allProjects', {
//           headers: {
//             'x-company-id': '67ce66294f28f85e4ff91e2c'
//           }
//         });
//         setProjectList(response.data.projects); // Assuming the response contains a 'projects' array
//       } catch (error) {
//         console.error('Error fetching projects:', error.response?.data || error.message);
//       }
//     };

//     fetchProjects();
//   }, []);

//   // Handle project click to navigate to detail page
//   const handleProjectClick = (project) => {
//     navigate('/detail', { state: { project } });
//   };

//   // Handle edit icon click
//   const handleEditClick = (project, e) => {
//     e.stopPropagation(); // Prevent row click event from firing
//     onEditProject(project); // Trigger the edit functionality
//   };

//   // Handle filtering
//   const handleFilter = (key, value) => {
//     setFilters((prevFilters) => ({
//       ...prevFilters,
//       [key]: value,
//     }));
//     setFilterDropdown(null); // Close dropdown after selecting a filter
//   };

//   // Remove a filter
//   const removeFilter = (key) => {
//     setFilters((prevFilters) => {
//       const newFilters = { ...prevFilters };
//       delete newFilters[key];
//       return newFilters;
//     });
//   };

//   // Reset all filters
//   const resetFilters = () => {
//     setFilters({});
//   };

//   // Handle sorting
//   const handleSort = (key) => {
//     let direction = "asc";
//     if (sortConfig.key === key && sortConfig.direction === "asc") {
//       direction = "desc";
//     }
//     setSortConfig({ key, direction });
//   };

//   // Filtered and sorted data
//   const filteredData = projectList.filter((item) => {
//     return Object.keys(filters).every((key) => {
//       if (!filters[key]) return true; // No filter applied for this key
//       const itemValue = item[key];
//       const filterValue = filters[key];
//       if (typeof itemValue === "number") {
//         return itemValue === parseFloat(filterValue); // Handle numeric comparison
//       }
//       return String(itemValue).toLowerCase().includes(filterValue.toLowerCase());
//     });
//   });

//   // Sort data
//   const sortedData = [...filteredData].sort((a, b) => {
//     if (sortConfig.key) {
//       const aValue = a[sortConfig.key];
//       const bValue = b[sortConfig.key];
//       if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
//       if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
//     }
//     return 0;
//   });

//   // Pagination logic
//   const totalPages = Math.ceil(sortedData.length / itemsPerPage);
//   const currentData = sortedData.slice(
//     (currentPage - 1) * itemsPerPage,
//     currentPage * itemsPerPage
//   );

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//   };

//   // Format currency
//   const formatCurrency = (value) => {
//     return new Intl.NumberFormat("en-IN", {
//       style: "currency",
//       currency: "INR",
//     }).format(value);
//   };

//   // Get unique values for a column (limited to 4 values)
//   // const getUniqueValues = (key) => {
//   //   const values = projectList.map((item) => item[key]);
//   //   const uniqueValues = [...new Set(values)].filter((value) => {
//   //     if (typeof value === "number") {
//   //       return value.toString().includes(searchQuery); // Handle numeric values
//   //     }
//   //     return value.toLowerCase().includes(searchQuery.toLowerCase());
//   //   });
//   //   return uniqueValues.slice(0, 4); // Show only 4 values
//   // };


//   const getUniqueValues = (key) => {
//     const values = projectList.map((item) => item[key]);
//     const uniqueValues = [...new Set(values)].filter((value) => {
//       if (value === undefined || value === null) return false; // Skip undefined or null values
//       if (typeof value === "number") {
//         return value.toString().includes(searchQuery); // Handle numeric values
//       }
//       return String(value).toLowerCase().includes(searchQuery.toLowerCase());
//     });
//     return uniqueValues.slice(0, 4); // Show only 4 values
//   };

//   // Function to get short name for country, state, and city
//   const getShortName = (value) => {
//     if (value === "India") return "IN";
//     if (value === "United States") return "US";
//     if (value === "United Kingdom") return "UK";
//     if (value === "Maharashtra") return "MH";
//     if (value === "California") return "CA";
//     if (value === "New York") return "NY";
//     if (value === "Mumbai") return "MUM";
//     if (value === "Los Angeles") return "LA";
//     return value; // Default to full name if no short name is defined
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return "";
//     const date = new Date(dateString);
//     return date.toLocaleDateString("en-GB"); // 'en-GB' formats as DD/MM/YYYY
//   };

//   // Close dropdown when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setFilterDropdown(null); // Close dropdown
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   const handledelete = async (e, id) => {
//     e.stopPropagation();
//     dispatch(deleteProject(id));
//   };

//   return (
//     <div className="overflow-hidden w-full h-[100vh] bg-white shadow-md rounded-md">
//       <ul className="border border-gray-300 rounded-md overflow-hidden">
//         {/* Table Header */}
//         <li className="flex bg-gray-100 font-semibold text-sm text-gray-700 border-b border-gray-300">
//           {[
//             "project_name",
//             "sector",
//             "cost",
//             "status",
//             "country",
//             "state",
//             "city",
//             "startDate",
//             "endDate",
//             "contractor",
//             "action", // Action column
//           ].map((header, index) => (
//             <div key={index} className="flex-1 px-4 py-2 flex flex-col items-start relative border-r border-gray-200">
//               <div className="flex items-center w-full">
//                 <span className="text-xs font-semibold uppercase mr-2">
//                   {header.replace(/_/g, " ")}
//                 </span>
//                 {header !== "action" && (
//                   <div className="flex items-center space-x-1">
//                     <button
//                       onClick={() => setFilterDropdown(header === filterDropdown ? null : header)}
//                       className="p-1 hover:bg-gray-200 rounded"
//                     >
//                       <Filter className="w-4 h-4 text-gray-500" />
//                     </button>
//                     {/* Render sorting button only for the "cost" column */}
//                     {header === "cost" && (
//                       <button
//                         onClick={() => handleSort(header)}
//                         className="p-1 hover:bg-gray-200 rounded"
//                       >
//                         <ArrowUpDown className="w-4 h-4 text-gray-500" />
//                       </button>
//                     )}
//                   </div>
//                 )}
//               </div>
//               {filterDropdown === header && (
//                 <div
//                   ref={dropdownRef}
//                   className="absolute top-full left-0 bg-white border border-gray-200 rounded-md shadow-lg z-10 w-48 max-h-48 overflow-y-auto"
//                 >
//                   <div className="p-2">
//                     {/* Search Bar */}
//                     <div className="flex items-center border border-gray-300 rounded-md mb-2">
//                       <input
//                         type="text"
//                         placeholder="Search..."
//                         value={searchQuery}
//                         onChange={(e) => setSearchQuery(e.target.value)}
//                         className="w-full p-1 text-sm outline-none"
//                       />
//                       <Search className="w-4 h-4 text-gray-500 mx-1" />
//                     </div>
//                     {/* Filter Options */}
//                     {getUniqueValues(header).map((value) => (
//                       <div
//                         key={value}
//                         onClick={() => handleFilter(header, value)}
//                         className="p-1 hover:bg-gray-100 cursor-pointer"
//                       >
//                         {value}
//                       </div>
//                     ))}
//                   </div>
//                 </div>
//               )}
//             </div>
//           ))}
//         </li>

//         {/* Applied Filters */}
//         {Object.keys(filters).length > 0 && (
//           <li className="flex items-center p-2 bg-gray-50 border-b border-gray-200">
//             <span className="text-sm text-gray-600 mr-2">Filters:</span>
//             {Object.entries(filters).map(([key, value]) => (
//               <div key={key} className="flex items-center bg-gray-200 rounded-full px-3 py-1 text-sm mr-2">
//                 <span>{key}: {value}</span>
//                 <button
//                   onClick={() => removeFilter(key)}
//                   className="ml-2 p-1 hover:bg-gray-300 rounded-full"
//                 >
//                   <X className="w-3 h-3" />
//                 </button>
//               </div>
//             ))}
//             <button
//               onClick={resetFilters}
//               className="text-sm text-blue-500 hover:underline"
//             >
//               Clear All
//             </button>
//           </li>
//         )}

//         {/* Table Rows */}
//         {currentData.length > 0 ? (
//           currentData.map((item) => (
//             <li
//               key={item._id}
//               className="flex border-b border-gray-200 hover:bg-gray-50 cursor-pointer transition-all"
//               onClick={() => handleProjectClick(item)} // Navigate to detail page on row click
//             >
//               {[
//                 item.project_name,
//                 item.sector,
//                 formatCurrency(item.cost),
//                 item.status,
//                 getShortName(item.country), // Short name for country
//                 getShortName(item.state), // Short name for state
//                 getShortName(item.city), // Short name for city
//                 // item.startDate,
//                 formatDate(item.startDate),
//                 formatDate(item.endDate),
//                 // item.endDate,
//                 item.contractor,
//                 <div key="action" className="flex space-x-2 items-center justify-center">
//                   <button
//                     onClick={(e) => handleEditClick(item, e)} // Handle edit icon click
//                     className="text-blue-500 hover:text-blue-700"
//                   >
//                     <Edit className="w-4 h-4" />
//                   </button>
//                   <button className="text-red-500 hover:text-red-700" onClick={(e) => handledelete(e, item._id)}>
//                     <Trash2 className="w-4 h-4" />
//                   </button>
//                 </div>, // Action buttons (pencil and trash icons)
//               ].map((value, index) => (
//                 <div key={index} className="flex-1 px-4 py-2 text-sm text-gray-800 border-r border-gray-200">
//                   {value}
//                 </div>
//               ))}
//             </li>
//           ))
//         ) : (
//           <li className="text-center py-4 text-gray-500">No data available</li>
//         )}
//       </ul>

//       {/* Pagination */}
//       <div className="flex justify-between py-4 items-center px-4">
//         <button
//           onClick={() => handlePageChange(currentPage - 1)}
//           className="mx-2 px-4 py-2 rounded-md bg-blue-500 text-white flex items-center disabled:opacity-50"
//           disabled={currentPage === 1}
//         >
//           <ChevronLeft className="w-4 h-4 mr-1" /> Previous
//         </button>
//         <div className="flex space-x-2">
//           {Array.from({ length: totalPages }, (_, index) => (
//             <button
//               key={index}
//               onClick={() => handlePageChange(index + 1)}
//               className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${currentPage === index + 1 ? "bg-blue-500 text-white" : "bg-gray-200"
//                 }`}
//             >
//               {index + 1}
//             </button>
//           ))}
//         </div>
//         <button
//           onClick={() => handlePageChange(currentPage + 1)}
//           className="mx-2 px-4 py-2 rounded-md bg-blue-500 text-white flex items-center disabled:opacity-50"
//           disabled={currentPage === totalPages}
//         >
//           Next <ChevronRight className="w-4 h-4 ml-1" />
//         </button>
//       </div>
//     </div>
//   );
// };

// export default ProjectList;


// import React, { useState, useEffect, useRef } from "react";
// import axios from "axios";
// import { ChevronLeft, ChevronRight, Filter, X, Search, Edit, Trash2, ArrowUpDown } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { deleteProject } from "../../FeatureRedux/project/deleteProject";
// import axiosInstance from "../axiosInstance";

// const ProjectList = ({ onEditProject }) => {
//   const [projectList, setProjectList] = useState([]);
//   const [filters, setFilters] = useState({});
//   const [currentPage, setCurrentPage] = useState(1);
//   const [filterDropdown, setFilterDropdown] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
//   const itemsPerPage = 10;
//   const navigate = useNavigate();
//   const dropdownRef = useRef(null);

//   const dispatch = useDispatch();
//   const { isLoading, isError, errorMessage } = useSelector((state) => state.deleteProject);

//   // Fetch projects from the backend
//   useEffect(() => {
//     const fetchProjects = async () => {
//       try {
//         const response = await axiosInstance.get('http://localhost:3002/api/project/allProjects', {
//           headers: {
//             'x-company-id': '67ce66294f28f85e4ff91e2c'
            
//           }
//         });
//         setProjectList(response.data.projects); // Assuming the response contains a 'projects' array
//       } catch (error) {
//         console.error('Error fetching projects:', error.response?.data || error.message);
//       }
//     };

//     fetchProjects();
//   }, []);

//   // Handle project click to navigate to detail page
//   const handleProjectClick = (project) => {
//     navigate('/detail', { state: { project } });
//   };

//   // Handle edit icon click
//   const handleEditClick = (project, e) => {
//     e.stopPropagation(); // Prevent row click event from firing
//     onEditProject(project); // Trigger the edit functionality
//   };

//   // Handle filtering
//   const handleFilter = (key, value) => {
//     setFilters((prevFilters) => ({
//       ...prevFilters,
//       [key]: value,
//     }));
//     setFilterDropdown(null); // Close dropdown after selecting a filter
//   };

//   // Remove a filter
//   const removeFilter = (key) => {
//     setFilters((prevFilters) => {
//       const newFilters = { ...prevFilters };
//       delete newFilters[key];
//       return newFilters;
//     });
//   };

//   // Reset all filters
//   const resetFilters = () => {
//     setFilters({});
//   };

//   // Handle sorting
//   const handleSort = (key) => {
//     let direction = "asc";
//     if (sortConfig.key === key && sortConfig.direction === "asc") {
//       direction = "desc";
//     }
//     setSortConfig({ key, direction });
//   };

//   // Filtered and sorted data
//   const filteredData = projectList.filter((item) => {
//     return Object.keys(filters).every((key) => {
//       if (!filters[key]) return true; // No filter applied for this key
//       const itemValue = item[key];
//       const filterValue = filters[key];
//       if (typeof itemValue === "number") {
//         return itemValue === parseFloat(filterValue); // Handle numeric comparison
//       }
//       return String(itemValue).toLowerCase().includes(filterValue.toLowerCase());
//     });
//   });

//   // Sort data
//   const sortedData = [...filteredData].sort((a, b) => {
//     if (sortConfig.key) {
//       const aValue = a[sortConfig.key];
//       const bValue = b[sortConfig.key];
//       if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
//       if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
//     }
//     return 0;
//   });

//   // Pagination logic
//   const totalPages = Math.ceil(sortedData.length / itemsPerPage);
//   const currentData = sortedData.slice(
//     (currentPage - 1) * itemsPerPage,
//     currentPage * itemsPerPage
//   );

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//   };

//   // Format currency
//   const formatCurrency = (value) => {
//     return new Intl.NumberFormat("en-IN", {
//       style: "currency",
//       currency: "INR",
//     }).format(value);
//   };

//   const getUniqueValues = (key) => {
//     const values = projectList.map((item) => item[key]);
//     const uniqueValues = [...new Set(values)].filter((value) => {
//       if (value === undefined || value === null) return false; // Skip undefined or null values
//       if (typeof value === "number") {
//         return value.toString().includes(searchQuery); // Handle numeric values
//       }
//       return String(value).toLowerCase().includes(searchQuery.toLowerCase());
//     });
//     return uniqueValues.slice(0, 4); // Show only 4 values
//   };

//   // Function to get short name for country, state, and city
//   const getShortName = (value) => {
//     if (value === "India") return "IN";
//     if (value === "United States") return "US";
//     if (value === "United Kingdom") return "UK";
//     if (value === "Maharashtra") return "MH";
//     if (value === "California") return "CA";
//     if (value === "New York") return "NY";
//     if (value === "Mumbai") return "MUM";
//     if (value === "Los Angeles") return "LA";
//     return value; // Default to full name if no short name is defined
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return "";
//     const date = new Date(dateString);
//     return date.toLocaleDateString("en-GB"); // 'en-GB' formats as DD/MM/YYYY
//   };

//   // Close dropdown when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setFilterDropdown(null); // Close dropdown
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   const handledelete = async (e, id) => {
//     e.stopPropagation();
//     dispatch(deleteProject(id));
//   };

//   return (
//     <div className="overflow-auto w-full h-[75vh] bg-gray-900 shadow-md rounded-md  border-gray-300">
//       <table className="w-full border-collapse border border-gray-300">
//         {/* Table Header */}
//         <thead className="bg-gray-900 text-sm font-semibold text-white">
//           <tr>
//             {[
//               "project_name",
//               "sector",
//               "cost",
//               "status",
//               "country",
//               "state",
//               "city",
//               "startDate",
//               "endDate",
//               "contractor",
//               "action",
//             ].map((header) => (
//               <th key={header} className="p-2 border-r border-gray-300 text-left relative">
//                 <div className="flex items-center">
//                   <span className="uppercase mr-2">{header.replace(/_/g, " ")}</span>
//                   {header !== "action" && (
//                     <div className="flex items-center space-x-1">
//                       <button
//                         onClick={() => setFilterDropdown(header === filterDropdown ? null : header)}
//                         className="p-1 hover:bg-gray-800 rounded"
//                       >
//                         <Filter className="w-4 h-4 text-gray-500" />
//                       </button>
//                       {header === "cost" && (
//                         <button
//                           onClick={() => handleSort(header)}
//                           className="p-1 hover:bg-gray-800 rounded"
//                         >
//                           <ArrowUpDown className="w-4 h-4 text-gray-500" />
//                         </button>
//                       )}
//                     </div>
//                   )}
//                   {filterDropdown === header && (
//                     <div ref={dropdownRef} className="absolute top-full left-0 bg-gray-900 text-white border border-gray-200 rounded-md shadow-lg z-10 w-48 max-h-48 overflow-y-auto">
//                       <div className="p-2">
//                         <input
//                           type="text"
//                           placeholder="Search..."
//                           className="w-full p-1 border border-gray-300 rounded-md"
//                           value={searchQuery}
//                           onChange={(e) => setSearchQuery(e.target.value)}
//                         />
//                         {getUniqueValues(header).map((value) => (
//                           <div
//                             key={value}
//                             className="p-1 hover:bg-gray-800 cursor-pointer"
//                             onClick={() => handleFilter(header, value)}
//                           >
//                             {value}
//                           </div>
//                         ))}
//                       </div>
//                     </div>
//                   )}
//                 </div>
//               </th>
//             ))}
//           </tr>
//         </thead>

//         {/* Table Body */}
//         <tbody>
//           {currentData.length > 0 ? (
//             currentData.map((item) => (
//               <tr
//                 key={item._id}
//                 className="hover:bg-gray-800 cursor-pointer transition-all"
//                 onClick={() => handleProjectClick(item)}
//               >
//                 {[
//                   item.project_name,
//                   item.sector,
//                   formatCurrency(item.cost),
//                   item.status,
//                   getShortName(item.country),
//                   getShortName(item.state),
//                   getShortName(item.city),
//                   formatDate(item.startDate),
//                   formatDate(item.endDate),
//                   item.contractor,
//                   <div key="action" className="flex space-x-2 items-center justify-center">
//                     <button
//                       onClick={(e) => handleEditClick(item, e)}
//                       className="text-blue-500 hover:text-blue-700"
//                     >
//                       <Edit className="w-4 h-4" />
//                     </button>
//                     <button
//                       className="text-red-500 hover:text-red-700"
//                       onClick={(e) => handledelete(e, item._id)}
//                     >
//                       <Trash2 className="w-4 h-4" />
//                     </button>
//                   </div>,
//                 ].map((value, index) => (
//                   <td key={index} className="p-3 border border-gray-300 text-sm text-white">
//                     {value}
//                   </td>
//                 ))}
//               </tr>
//             ))
//           ) : (
//             <tr>
//               <td colSpan={11} className="text-center py-4 text-gray-500">
//                 No data available
//               </td>
//             </tr>
//           )}
//         </tbody>
//       </table>

//       {/* Pagination */}
//       <div className="flex justify-between py-4 items-center px-4">
//         <button
//           onClick={() => handlePageChange(currentPage - 1)}
//           className="mx-2 px-4 py-2 rounded-md bg-blue-500 text-white flex items-center disabled:opacity-50"
//           disabled={currentPage === 1}
//         >
//           <ChevronLeft className="w-4 h-4 mr-1" /> Previous
//         </button>

//         <div className="flex space-x-2">
//           {Array.from({ length: totalPages }, (_, index) => (
//             <button
//               key={index}
//               onClick={() => handlePageChange(index + 1)}
//               className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${currentPage === index + 1 ? "bg-blue-500 text-white" : "bg-gray-200"
//                 }`}
//             >
//               {index + 1}
//             </button>
//           ))}
//         </div>

//         <button
//           onClick={() => handlePageChange(currentPage + 1)}
//           className="mx-2 px-4 py-2 rounded-md bg-blue-500 text-white flex items-center disabled:opacity-50"
//           disabled={currentPage === totalPages}
//         >
//           Next <ChevronRight className="w-4 h-4 ml-1" />
//         </button>
//       </div>
//     </div>
//   );
// };

// export default ProjectList;




// new code .......... 
// import React, { useState, useEffect, useRef } from "react";
// import axios from "axios";
// import { ChevronLeft, ChevronRight, Filter, X, Search, Edit, Trash2, ArrowUpDown } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { deleteProject } from "../../FeatureRedux/project/deleteProject";
// import axiosInstance from "../axiosInstance";

// const ProjectList = ({ onEditProject }) => {
//   const [projectList, setProjectList] = useState([]);
//   const [filters, setFilters] = useState({});
//   const [currentPage, setCurrentPage] = useState(1);
//   const [filterDropdown, setFilterDropdown] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [projectToDelete, setProjectToDelete] = useState(null);
//   const itemsPerPage = 10;
//   const navigate = useNavigate();
//   const dropdownRef = useRef(null);
//   const modalRef = useRef(null);

//   const dispatch = useDispatch();
//   const { isLoading, isError, errorMessage } = useSelector((state) => state.deleteProject);

//   // Fetch projects from the backend
//   useEffect(() => {
//     const fetchProjects = async () => {
//       try {
//         const response = await axiosInstance.get('http://localhost:3002/api/project/allProjects', {
//           headers: {
//             'x-company-id': '67ce66294f28f85e4ff91e2c'
//           }
//         });
//         setProjectList(response.data.projects);
//       } catch (error) {
//         console.error('Error fetching projects:', error.response?.data || error.message);
//       }
//     };

//     fetchProjects();
//   }, []);

//   // Close dropdown/modal when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setFilterDropdown(null);
//       }
//       if (modalRef.current && !modalRef.current.contains(event.target)) {
//         setShowDeleteModal(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   // Handle project click to navigate to detail page
//   const handleProjectClick = (project) => {
//     navigate('/detail', { state: { project } });
//   };

//   // Handle edit icon click
//   const handleEditClick = (project, e) => {
//     e.stopPropagation();
//     onEditProject(project);
//   };

//   // Handle delete confirmation
//   const handleDeleteClick = (e, project) => {
//     e.stopPropagation();
//     setProjectToDelete(project);
//     setShowDeleteModal(true);
//   };

//   // Confirm delete action
//   const confirmDelete = () => {
//     if (projectToDelete) {
//       dispatch(deleteProject(projectToDelete._id));
//       setShowDeleteModal(false);
//       setProjectToDelete(null);
//     }
//   };

//   // Cancel delete action
//   const cancelDelete = () => {
//     setShowDeleteModal(false);
//     setProjectToDelete(null);
//   };

//   // Handle filtering
//   const handleFilter = (key, value) => {
//     setFilters((prevFilters) => ({
//       ...prevFilters,
//       [key]: value,
//     }));
//     setFilterDropdown(null);
//   };

//   // Remove a filter
//   const removeFilter = (key) => {
//     setFilters((prevFilters) => {
//       const newFilters = { ...prevFilters };
//       delete newFilters[key];
//       return newFilters;
//     });
//   };

//   // Reset all filters
//   const resetFilters = () => {
//     setFilters({});
//   };

//   // Handle sorting
//   const handleSort = (key) => {
//     let direction = "asc";
//     if (sortConfig.key === key && sortConfig.direction === "asc") {
//       direction = "desc";
//     }
//     setSortConfig({ key, direction });
//   };

//   // Filtered and sorted data
//   const filteredData = projectList.filter((item) => {
//     return Object.keys(filters).every((key) => {
//       if (!filters[key]) return true;
//       const itemValue = item[key];
//       const filterValue = filters[key];
      
//       if (itemValue == null || filterValue == null) return false;
      
//       if (typeof itemValue === "number") {
//         return itemValue === parseFloat(filterValue);
//       }
      
//       const itemStr = String(itemValue).toLowerCase();
//       const filterStr = String(filterValue).toLowerCase();
      
//       return itemStr.includes(filterStr);
//     });
//   });

//   // Sort data
//   const sortedData = [...filteredData].sort((a, b) => {
//     if (sortConfig.key) {
//       const aValue = a[sortConfig.key];
//       const bValue = b[sortConfig.key];
//       if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
//       if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
//     }
//     return 0;
//   });

//   // Pagination logic
//   const totalPages = Math.ceil(sortedData.length / itemsPerPage);
//   const currentData = sortedData.slice(
//     (currentPage - 1) * itemsPerPage,
//     currentPage * itemsPerPage
//   );

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//   };

//   // Format currency
//   const formatCurrency = (value) => {
//     return new Intl.NumberFormat("en-IN", {
//       style: "currency",
//       currency: "INR",
//     }).format(value);
//   };

//   const getUniqueValues = (key) => {
//     const values = projectList.map((item) => item[key]);
//     const uniqueValues = [...new Set(values)].filter((value) => {
//       if (value === undefined || value === null) return false;
//       if (typeof value === "number") {
//         return value.toString().includes(searchQuery);
//       }
//       return String(value).toLowerCase().includes(searchQuery.toLowerCase());
//     });
//     return uniqueValues.slice(0, 4);
//   };

//   // Function to get short name for country, state, and city
//   const getShortName = (value) => {
//     if (value === "India") return "IN";
//     if (value === "United States") return "US";
//     if (value === "United Kingdom") return "UK";
//     if (value === "Maharashtra") return "MH";
//     if (value === "California") return "CA";
//     if (value === "New York") return "NY";
//     if (value === "Mumbai") return "MUM";
//     if (value === "Los Angeles") return "LA";
//     return value;
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return "";
//     const date = new Date(dateString);
//     return date.toLocaleDateString("en-GB");
//   };

//   return (
//     <div className="relative overflow-auto w-full h-[75vh] bg-gray-900 shadow-md rounded-md border-gray-300">
//       {/* Delete Confirmation Modal */}
//       {showDeleteModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div 
//             ref={modalRef}
//             className="bg-gray-800 rounded-lg p-6 w-full max-w-md transform transition-all"
//           >
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold text-white">Confirm Deletion</h3>
//               <button 
//                 onClick={cancelDelete}
//                 className="text-gray-400 hover:text-white"
//               >
//                 <X className="w-6 h-6" />
//               </button>
//             </div>
//             <p className="text-gray-300 mb-6">
//               Are you sure you want to delete project "{projectToDelete?.project_name}"? This action cannot be undone.
//             </p>
//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={cancelDelete}
//                 className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition-colors"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={confirmDelete}
//                 className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center"
//                 disabled={isLoading}
//               >
//                 {isLoading ? (
//                   <>
//                     <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                       <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                       <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                     </svg>
//                     Deleting...
//                   </>
//                 ) : (
//                   <>
//                     <Trash2 className="w-4 h-4 mr-2" />
//                     Delete
//                   </>
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <table className="w-full border-collapse border border-gray-300">
//         {/* Table Header */}
//         <thead className="bg-gray-900 text-sm font-semibold text-white">
//           <tr>
//             {[
//               "project_name",
//               "sector",
//               "cost",
//               "status",
//               "country",
//               "state",
//               "city",
//               "startDate",
//               "endDate",
//               "contractor",
//               "action",
//             ].map((header) => (
//               <th key={header} className="p-2 border-r border-gray-300 text-left relative">
//                 <div className="flex items-center">
//                   <span className="uppercase mr-2">{header.replace(/_/g, " ")}</span>
//                   {header !== "action" && (
//                     <div className="flex items-center space-x-1">
//                       <button
//                         onClick={() => setFilterDropdown(header === filterDropdown ? null : header)}
//                         className="p-1 hover:bg-gray-800 rounded"
//                       >
//                         <Filter className="w-4 h-4 text-gray-500" />
//                       </button>
//                       {header === "cost" && (
//                         <button
//                           onClick={() => handleSort(header)}
//                           className="p-1 hover:bg-gray-800 rounded"
//                         >
//                           <ArrowUpDown className="w-4 h-4 text-gray-500" />
//                         </button>
//                       )}
//                     </div>
//                   )}
//                   {filterDropdown === header && (
//                     <div ref={dropdownRef} className="absolute top-full left-0 bg-gray-900 text-white border border-gray-200 rounded-md shadow-lg z-10 w-48 max-h-48 overflow-y-auto">
//                       <div className="p-2">
//                         <input
//                           type="text"
//                           placeholder="Search..."
//                           className="w-full p-1 border border-gray-300 rounded-md"
//                           value={searchQuery}
//                           onChange={(e) => setSearchQuery(e.target.value)}
//                         />
//                         {getUniqueValues(header).map((value) => (
//                           <div
//                             key={value}
//                             className="p-1 hover:bg-gray-800 cursor-pointer"
//                             onClick={() => handleFilter(header, value)}
//                           >
//                             {value}
//                           </div>
//                         ))}
//                       </div>
//                     </div>
//                   )}
//                 </div>
//               </th>
//             ))}
//           </tr>
//         </thead>

//         {/* Table Body */}
//         <tbody>
//           {currentData.length > 0 ? (
//             currentData.map((item) => (
//               <tr
//                 key={item._id}
//                 className="hover:bg-gray-800 cursor-pointer transition-all"
//                 onClick={() => handleProjectClick(item)}
//               >
//                 {[
//                   item.project_name,
//                   item.sector,
//                   formatCurrency(item.cost),
//                   item.status,
//                   getShortName(item.country),
//                   getShortName(item.state),
//                   getShortName(item.city),
//                   formatDate(item.startDate),
//                   formatDate(item.endDate),
//                   item.contractor,
//                   <div key="action" className="flex space-x-2 items-center justify-center">
//                     <button
//                       onClick={(e) => handleEditClick(item, e)}
//                       className="text-blue-500 hover:text-blue-700"
//                     >
//                       <Edit className="w-4 h-4" />
//                     </button>
//                     <button
//                       className="text-red-500 hover:text-red-700"
//                       onClick={(e) => handleDeleteClick(e, item)}
//                     >
//                       <Trash2 className="w-4 h-4" />
//                     </button>
//                   </div>,
//                 ].map((value, index) => (
//                   <td key={index} className="p-3 border border-gray-300 text-sm text-white">
//                     {value}
//                   </td>
//                 ))}
//               </tr>
//             ))
//           ) : (
//             <tr>
//               <td colSpan={11} className="text-center py-4 text-gray-500">
//                 No data available
//               </td>
//             </tr>
//           )}
//         </tbody>
//       </table>

//       {/* Pagination */}
//       <div className="flex justify-between py-4 items-center px-4">
//         <button
//           onClick={() => handlePageChange(currentPage - 1)}
//           className="mx-2 px-4 py-2 rounded-md bg-blue-500 text-white flex items-center disabled:opacity-50"
//           disabled={currentPage === 1}
//         >
//           <ChevronLeft className="w-4 h-4 mr-1" /> Previous
//         </button>

//         <div className="flex space-x-2">
//           {Array.from({ length: totalPages }, (_, index) => (
//             <button
//               key={index}
//               onClick={() => handlePageChange(index + 1)}
//               className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${currentPage === index + 1 ? "bg-blue-500 text-white" : "bg-gray-200"
//                 }`}
//             >
//               {index + 1}
//             </button>
//           ))}
//         </div>

//         <button
//           onClick={() => handlePageChange(currentPage + 1)}
//           className="mx-2 px-4 py-2 rounded-md bg-blue-500 text-white flex items-center disabled:opacity-50"
//           disabled={currentPage === totalPages}
//         >
//           Next <ChevronRight className="w-4 h-4 ml-1" />
//         </button>
//       </div>
//     </div>
//   );
// };

// export default ProjectList;


